import React, { useState, useEffect, useRef } from "react";
import {
  Camera,
  Check,
  Clock,
  Sparkles,
  ShoppingCart,
  MessageCircle,
  Brain,
  Search,
  Database,
  Lightbulb,
  Wand2,
  Share2,
  Copy,
  Heart,
  Gift,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import {
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import { useConsultation } from "./hooks/useConsultation";
import { ConsultationService } from "./services/consultationService";
import { WebhookService } from "./services/webhookService";
import SkinAnalysisDisplay from "./components/SkinAnalysisDisplay";
import HairRoutineDisplay from "./components/HairRoutineDisplay";
import ChatWidget from "./components/ChatWidget";
import LoadingWrapper from "./components/LoadingWrapper";
import ToastNotification from "./components/ToastNotification";
import { generateSubmissionId } from "./utils/uuid";
import {
  supabase,
  detectInitialStep,
  getStoredFormData,
  storeFormData,
  storeCurrentStep,
  getStoredCountdownState,
  storeCountdownState,
  clearCountdownState,
  getStoredAppState,
  storeAppState,
  storeLastProductUpdate,
  getStoredUserId,
  startNewConsultationSession,
  getStoredUploadSuccess,
  setStoredUploadSuccess,
} from "./lib/supabase";

const WonderBeautiesConsultation = () => {
  // ========================================
  // FEATURE FLAG: Set to false to simplify flow (Step 1 → Step 2 → Thank You Message)
  // Set to true to enable full flow with AI analysis, products, and ordering
  // ========================================
  const ENABLE_FULL_FLOW = false;

  // Detect initial step synchronously to prevent flashing
  const [currentStep, setCurrentStep] = useState(() => detectInitialStep());
  const [uploadedImage, setUploadedImage] = useState(null);
  const imageInputRef = useRef(null);

  // Initialize countdown with restored state (but only if we're actually in countdown phase)
  const [countdown, setCountdown] = useState(() => {
    const appState = getStoredAppState();
    // Don't show countdown if we're in advanced states
    if (appState && (appState.showRoutine || appState.aiGeneratedProducts)) {
      return 0; // Skip countdown
    }
    const countdownState = getStoredCountdownState();
    return countdownState && !countdownState.expired
      ? countdownState.remaining
      : 10;
  });

  // Add loading state for data restoration
  const [initialLoading, setInitialLoading] = useState(true);

  // Initialize advanced states from localStorage
  const [showRoutine, setShowRoutine] = useState(() => {
    const appState = getStoredAppState();
    return appState?.showRoutine || false;
  });
  const [aiGeneratedProducts, setAiGeneratedProducts] = useState(() => {
    const appState = getStoredAppState();
    return appState?.aiGeneratedProducts || null;
  });
  const [showPharmacistMessage, setShowPharmacistMessage] = useState(() => {
    const appState = getStoredAppState();
    return appState?.showPharmacistMessage || false;
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [aiStep, setAiStep] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [aiProgress, setAiProgress] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [showCompletionPage, setShowCompletionPage] = useState(() => {
    const appState = getStoredAppState();
    return appState?.showCompletionPage || false;
  });
  const [showSkinReport, setShowSkinReport] = useState(false);
  const [showSkinReportPreview, setShowSkinReportPreview] = useState(false);
  const [hasDataError, setHasDataError] = useState(false);

  // Consultation type: 'Skin Care' or 'Hair Care'
  const [consultationType, setConsultationType] = useState(() => {
    const appState = getStoredAppState();
    return appState?.consultationType || 'Skin Care';
  });
  const [hairRoutineData, setHairRoutineData] = useState(() => {
    const appState = getStoredAppState();
    return appState?.hairRoutineData || null;
  });
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [orderData, setOrderData] = useState({
    address: "",
    city: "بغداد",
  });

  // Real-time polling states
  const [pollingPhase, setPollingPhase] = useState(1); // 1: dermatologist analysis, 2: product search
  const [dermatologistResults, setDermatologistResults] = useState(null);
  const [dermatologistResultsHair, setDermatologistResultsHair] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [showTimeout, setShowTimeout] = useState(false);
  const [realTimeAnalyzing, setRealTimeAnalyzing] = useState(false);
  const [currentJokeIndex, setCurrentJokeIndex] = useState(0);
  const [pollingError, setPollingError] = useState(null);

  // Add chat widget state
  const [showChat, setShowChat] = useState(false);
  const [chatConfig, setChatConfig] = useState({
    startOpen: false,
    initialMessage: "",
  });

  // Chat engagement state management
  const [chatPromptShown, setChatPromptShown] = useState({
    topOfPage: false,
    endOfProducts: false,
    userInteracted: false, // Track if user already opened chat
  });

  // Add state to control AI upsell section visibility
  const [showAiUpsell, setShowAiUpsell] = useState(true);

  // Budget selection state
  const [budgetRanges, setBudgetRanges] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);

  // Add Supabase integration
  const {
    loading: dbLoading,
    error: dbError,
    uploadSessionImage,
    saveCompleteConsultation,
    consultationData,
    getFreshConsultationData,
    clearError,
    setErrorMessage,
  } = useConsultation();

  const [formData, setFormData] = useState(() => {
    const storedData = getStoredFormData();
    if (storedData) {
      return storedData;
    }
    return {
      name: "",
      phone: "",
      skinType: "",
      relationshipStatus: "",
      skinSensitivity: [],
      notSensitive: false,
      additionalSensitivity: "",
    };
  });

  // Country selector state for phone input
  const [selectedCountry, setSelectedCountry] = useState("IQ"); // Default to Iraq

  // Add image upload loading state
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(() => {
    const storedSuccess = getStoredUploadSuccess();
    console.log(
      "🔄 Initializing uploadSuccess from localStorage:",
      storedSuccess
    );
    return storedSuccess;
  });

  // Add user creation loading state
  const [creatingUser, setCreatingUser] = useState(false);

  // Add form submission loading state
  const [submittingForm, setSubmittingForm] = useState(false);

  // Toast notification state for webhook submissions
  const [toastStatus, setToastStatus] = useState(null); // null, 'pending', 'success', 'failed'
  const [toastMessage, setToastMessage] = useState("");
  const [webhookRetryCallback, setWebhookRetryCallback] = useState(null);

  // Channel mapping for multi-channel support (Instagram, WhatsApp, etc.)
  // Extract channel params synchronously to avoid race conditions during initialization
  const [channelParams, setChannelParams] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contact_id = urlParams.get("contact_id");
    const channel_id = urlParams.get("channel_id");

    if (contact_id && channel_id) {
      console.log("📱 Channel params detected (sync):", {
        contact_id,
        channel_id,
      });
      return { contact_id, channel_id };
    } else {
      console.log("ℹ️ No channel params in URL - generic link mode");
      return { contact_id: null, channel_id: null };
    }
  });

  // Product update states - SIMPLIFIED
  const [refreshingProducts, setRefreshingProducts] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshSuccess, setRefreshSuccess] = useState(false);

  // Initialize component with existing data on mount
  useEffect(() => {
    const initializeComponent = async () => {
      console.log("🔄 Initializing component with step:", currentStep);
      setInitialLoading(true);

      // ===== PRIVACY CHECK: Detect shared link with channel params =====
      // Strategy: Check if link has been "used" (image uploaded) by querying database
      // - First-time user: No images in DB → allow access (keep URL params)
      // - Original user: Has images + localStorage matches → allow access
      // - Friend (shared): Has images + localStorage doesn't match → redirect to clean URL
      const urlParams = new URLSearchParams(window.location.search);
      const urlContactId = urlParams.get("contact_id");
      const urlChannelId = urlParams.get("channel_id");

      if (urlContactId && urlChannelId) {
        //         console.log(
        //           "🔍 Checking if link has been used (contact_id:",
        //           urlContactId,
        //           "channel_id:",
        //           urlChannelId,
        //           ")"
        //         );

        try {
          // Step 1: Find user_id from channel mappings
          const { data: channelMapping, error: mappingError } = await supabase
            .from("consultation_channel_mappings")
            .select("user_id")
            .eq("contact_id", urlContactId)
            .eq("channel_id", urlChannelId)
            .maybeSingle();

          if (mappingError) {
            console.log(
              "ℹ️ No channel mapping found (first-time user or error):",
              mappingError.message
            );
            // No mapping = first-time user, allow access
          } else if (channelMapping?.user_id) {
            console.log(
              "✅ Found user_id from channel mapping:",
              channelMapping.user_id
            );

            // Step 2: Check if this user has uploaded images (link has been used)
            const { data: images, error: imagesError } = await supabase
              .from("consultation_images")
              .select("id")
              .eq("user_id", channelMapping.user_id)
              .limit(1);

            if (imagesError) {
              console.warn("⚠️ Error checking images:", imagesError.message);
            } else if (images && images.length > 0) {
              // Link has been used (image uploaded)
              console.log("📸 Images found - link has been used");

              // Step 3: Check if current browser session matches
              const currentUserId = getStoredUserId();

              if (currentUserId !== channelMapping.user_id) {
                // Different user visiting (shared link) → redirect to protect privacy
                console.log(
                  "🔒 Shared link detected (user mismatch) - redirecting to main page"
                );
                console.log(
                  "   Current userId:",
                  currentUserId,
                  "| Expected userId:",
                  channelMapping.user_id
                );
                window.location.href = "/";
                return; // Stop execution
              }

              // Same user returning → allow access
              console.log(
                "✅ Original user returning (userId matches) - restoring session"
              );
            } else {
              // No images yet → first-time user → allow access
              console.log(
                "🆕 First-time user with custom link (no images yet) - allowing access"
              );
            }
          } else {
            // No mapping found → first-time user
            console.log(
              "🆕 No existing mapping - first-time user with custom link"
            );
          }
        } catch (error) {
          console.error("❌ Error during shared link check:", error);
          // On error, allow access to prevent blocking legitimate users
        }
      }

      // Handle countdown state for returning users
      const appState = getStoredAppState();

      // If completion page should be shown, skip all initialization
      if (appState?.showCompletionPage) {
        console.log("🎯 Completion page detected, showing completion page");
        setShowCompletionPage(true);
        setInitialLoading(false);
        return; // Skip further processing
      }

      // If user is in advanced state, skip countdown entirely
      if (
        appState &&
        (appState.showRoutine ||
          appState.aiGeneratedProducts ||
          appState.showPharmacistMessage)
      ) {
        console.log("🎯 User in advanced state, skipping countdown");
        setCountdown(0);
        clearCountdownState(); // Clean up any stale countdown
        setInitialLoading(false);
        return; // Skip further processing
      }

      // Handle normal countdown restoration
      const countdownState = getStoredCountdownState();
      if (countdownState && countdownState.expired && currentStep === 3) {
        setShowPharmacistMessage(true);
        setCountdown(0);
      }

      // Only load detailed data if we have a user beyond step 1
      if (currentStep > 1) {
        try {
          const existingData = await saveCompleteConsultation(
            {
              // Pass empty data but with loadOnly=true to prevent any updates
            },
            true,
            channelParams
          );

          if (
            existingData?.success &&
            existingData?.data &&
            (existingData.data.user || existingData.data.consultation)
          ) {
            console.log("📥 Loading detailed data for step:", currentStep);

            // Set consultation type from existing data
            const existingType = existingData.data.consultation?.consultation_type || 'Skin Care';
            setConsultationType(existingType);

            // Load AI-generated products if available
            const hasSkinProducts = !!existingData.data.consultation?.products_offered;
            const hairConsultation = existingData.data.consultation?.hair_user_consultations;
            const hasHairProducts = !!hairConsultation?.product_offer;

            if (hasSkinProducts) {
              const transformedProducts = transformAiProductData(
                existingData.data.consultation.products_offered
              );
              if (transformedProducts) {
                setAiGeneratedProducts(transformedProducts);
                setHasDataError(false);
              }
              // Set Skin Analysis
              if (existingData.data.consultation.dermatologist_analysis_ar) {
                setDermatologistResults(existingData.data.consultation.dermatologist_analysis_ar);
              }
            }

            if (hasHairProducts) {
              const transformedHairData = transformHairProductData(
                hairConsultation.product_offer
              );
              if (transformedHairData) {
                setHairRoutineData(transformedHairData);
                setHasDataError(false);
              }
              // Set Hair Analysis
              if (hairConsultation.dermatologist_analysis) {
                setDermatologistResultsHair(hairConsultation.dermatologist_analysis);
              }
            }

            // Set default consultation type for UI behavior
            if (existingType) {
              setConsultationType(existingType);
            } else if (hasHairProducts && !hasSkinProducts) {
              setConsultationType('Hair Care');
            } else {
              setConsultationType('Skin Care');
            }

            // Restore completion states
            if (existingData.data.consultation?.consultation_completed) {
              setShowPharmacistMessage(true);
              setAnalyzing(false);
              setShowRoutine(true);
            }

            // Set upload success if images exist
            if (existingData.data.images?.length > 0) {
              setUploadedImage({ url: "placeholder", file: null });
              setUploadSuccess(true);
            }
          }
        } catch (error) {
          console.log("ℹ️ Error loading detailed data:", error.message);
        }
      }

      // Store current step
      storeCurrentStep(currentStep);

      setInitialLoading(false);
    };

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Extract channel mapping parameters from URL (also update on URL changes)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contact_id = urlParams.get("contact_id");
    const channel_id = urlParams.get("channel_id");

    // Only update if different from current state
    if (contact_id && channel_id) {
      setChannelParams((prev) => {
        if (prev.contact_id !== contact_id || prev.channel_id !== channel_id) {
          console.log("📱 Channel params updated:", { contact_id, channel_id });
          return { contact_id, channel_id };
        }
        return prev;
      });
    } else {
      setChannelParams((prev) => {
        if (prev.contact_id !== null || prev.channel_id !== null) {
          console.log("ℹ️ Channel params cleared - generic link mode");
          return { contact_id: null, channel_id: null };
        }
        return prev;
      });
    }
  }, []); // Run once on mount

  // Initialize chat widget when user has data or reaches checkout
  useEffect(() => {
    // Show chat widget when user reaches step 3 (skin analysis) or has consultation data
    if (currentStep >= 3 || consultationData?.user) {
      setShowChat(true);
      console.log("💬 Chat widget enabled for step:", currentStep);
    }
  }, [currentStep, consultationData]);

  // Reset chat prompt states when showing routine (when user sees products)
  useEffect(() => {
    if (showRoutine) {
      // Reset prompt states for new product viewing session
      setChatPromptShown({
        topOfPage: false,
        endOfProducts: false,
        userInteracted: false,
      });
    }
  }, [showRoutine]);

  // Load consultation data when it changes
  // IMPORTANT: Only update formData if database has meaningful data, don't overwrite user's current input
  useEffect(() => {
    if (consultationData?.user && consultationData?.consultation) {
      // Only update formData if database has actual submitted data
      // Don't overwrite if user is still filling the form
      const hasSubmittedData =
        consultationData.user.name ||
        consultationData.user.phone ||
        consultationData.consultation.skin_type ||
        consultationData.consultation.relationship_status;

      if (hasSubmittedData) {
        // Parse phone number to extract country and local number
        let localPhone = consultationData.user.phone || "";
        let detectedCountry = "IQ"; // Default to Iraq

        if (localPhone && localPhone.startsWith("+")) {
          // Try to detect country from phone number
          const countries = getCountries();
          for (const country of countries) {
            const countryCode = getCountryCallingCode(country);
            if (localPhone.startsWith(`+${countryCode}`)) {
              detectedCountry = country;
              localPhone = localPhone.substring(countryCode.length + 1); // Remove +countryCode
              break;
            }
          }
        }

        setSelectedCountry(detectedCountry);
        // Merge with existing formData instead of overwriting completely
        setFormData((prev) => ({
          ...prev,
          name: consultationData.user.name || prev.name || "",
          phone: localPhone || prev.phone || "",
          skinType:
            consultationData.consultation.skin_type || prev.skinType || "",
          relationshipStatus:
            consultationData.consultation.relationship_status ||
            prev.relationshipStatus ||
            "",
          skinSensitivity:
            consultationData.consultation.skin_sensitivity ||
            prev.skinSensitivity ||
            [],
          notSensitive:
            consultationData.consultation.not_sensitive !== undefined
              ? consultationData.consultation.not_sensitive
              : prev.notSensitive,
          additionalSensitivity:
            consultationData.consultation.additional_sensitivity ||
            prev.additionalSensitivity ||
            "",
        }));
      }

      // Set consultation type
      const type = consultationData.consultation.consultation_type || 'Skin Care';
      setConsultationType(type);
      console.log("📋 Consultation type:", type);

      // Load AI-generated products if available
      const hasSkinProducts = !!consultationData.consultation.products_offered;
      const hairConsultation = consultationData.consultation.hair_user_consultations;
      const hasHairProducts = !!hairConsultation?.product_offer;

      if (hasSkinProducts) {
        console.log("📦 Loading Skin AI-generated products");
        const transformedProducts = transformAiProductData(
          consultationData.consultation.products_offered
        );
        if (transformedProducts) {
          setAiGeneratedProducts(transformedProducts);
          setHasDataError(false);
        }
        // Set Skin Analysis
        if (consultationData.consultation.dermatologist_analysis_ar) {
          setDermatologistResults(consultationData.consultation.dermatologist_analysis_ar);
        }
      }

      if (hasHairProducts) {
        console.log("📦 Loading Hair AI-generated products");
        const transformedHairData = transformHairProductData(
          hairConsultation.product_offer
        );
        if (transformedHairData) {
          setHairRoutineData(transformedHairData);
          setHasDataError(false);
        }
        // Set Hair Analysis
        if (hairConsultation.dermatologist_analysis) {
          setDermatologistResultsHair(hairConsultation.dermatologist_analysis);
        }
      }

      // If both exist, we might want to prioritize showing something or just keep current type
      if (!consultationType) {
        if (hasHairProducts && !hasSkinProducts) {
          setConsultationType('Hair Care');
        } else {
          setConsultationType('Skin Care');
        }
      }
    }

    // Load session images if available
    if (consultationData?.sessionImages?.length > 0 && !uploadedImage) {
      console.log("🖼️ Session images found:", consultationData.sessionImages);
      // Set upload success if we have session images
      setUploadSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationData]);

  // NO AUTO-CHECK - User requested manual refresh only

  // Auto-clear error messages after 5 seconds
  useEffect(() => {
    if (refreshError) {
      const timeoutId = setTimeout(() => {
        setRefreshError(null);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [refreshError]);

  // Auto-clear success messages after 3 seconds
  useEffect(() => {
    if (refreshSuccess) {
      const timeoutId = setTimeout(() => {
        setRefreshSuccess(false);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [refreshSuccess]);

  // Persist form data whenever it changes
  useEffect(() => {
    storeFormData(formData);
  }, [formData]);

  // Persist current step whenever it changes
  useEffect(() => {
    storeCurrentStep(currentStep);
  }, [currentStep]);

  // Load budget ranges from Supabase and set middle range as default
  useEffect(() => {
    async function loadBudgetRanges() {
      try {
        const { data, error } = await supabase
          .from("budget_ranges")
          .select("*")
          .eq("is_active", true)
          .order("display_order");

        if (error) {
          console.error("❌ Error loading budget ranges:", error);
          return;
        }

        if (data && data.length > 0) {
          setBudgetRanges(data);
          // Set middle range as default (index 1 for 3 ranges)
          const middleIndex = Math.floor(data.length / 2);
          setSelectedBudget(data[middleIndex]);
          console.log(
            "✅ Budget ranges loaded, default set to:",
            data[middleIndex].label_ar
          );
        }
      } catch (error) {
        console.error("❌ Failed to load budget ranges:", error);
      }
    }

    loadBudgetRanges();
  }, []);

  // Persist app state whenever critical states change
  useEffect(() => {
    const appState = {
      showRoutine,
      aiGeneratedProducts,
      hairRoutineData,
      consultationType,
      showPharmacistMessage,
      analyzing,
      orderSuccess,
      showCompletionPage,
    };
    storeAppState(appState);
  }, [
    showRoutine,
    aiGeneratedProducts,
    hairRoutineData,
    consultationType,
    showPharmacistMessage,
    analyzing,
    orderSuccess,
    showCompletionPage,
  ]);

  // AI Analysis Steps with Your Hilarious Phrases!
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const aiSteps = [
    {
      icon: Search,
      title: "شوفوا شو عندنا هون...",
      subtitle: "الوضع مو چبير… شوية حبوب عالچبين، بس مو فلم رعب.",
      color: "from-blue-400 to-blue-500",
      bgColor: "bg-blue-50",
      duration: 3000,
    },
    {
      icon: Brain,
      title: "تحليل حالة الزبونة...",
      subtitle:
        formData.relationshipStatus === "single"
          ? "هااا… زبونتنا سنگل! يلا نرتب إلها روتين يخلي بشرتها مثل المراية، مو بس الكوريين يفتهمون."
          : formData.relationshipStatus === "pregnant"
            ? "أوه، أميرتنا حامل! يلا نختار منتجات آمنة تخليها تتألق بحملها."
            : formData.relationshipStatus === "nursing"
              ? "ماما الحلوة! نختار مواد طبيعية ولطيفة، ما نريد نؤثر على الرضاعة."
              : "زبونتنا متزوجة ومستقرة، يلا نعطيها روتين متوازن يناسب حياتها العائلية.",
      color: "from-purple-400 to-purple-500",
      bgColor: "bg-purple-50",
      duration: 4000,
    },
    {
      icon: Lightbulb,
      title: "دراسة المشاكل بتمعن...",
      subtitle:
        "يلا يمعودين، شدّوا الحيل… لازم نلكه حل قبل لا يطب علينا الليل!",
      color: "from-yellow-400 to-orange-500",
      bgColor: "bg-yellow-50",
      duration: 3500,
    },
    {
      icon: Database,
      title: "البحث عن أفضل المواد...",
      subtitle: "حاضر! راح أدوّر عالمواد الفعّالة، خلّي العرسان يظلون بالدور.",
      color: "from-green-400 to-green-500",
      bgColor: "bg-green-50",
      duration: 4500,
    },
    {
      icon: Wand2,
      title: "تجميع الروتين النهائي...",
      subtitle:
        'أريد نتيجة تخلّي جيرانها يسألوها: "وين تسوين سكن كير؟ بتركيا لو بدبي؟"',
      color: "from-pink-400 to-rose-500",
      bgColor: "bg-pink-50",
      duration: 3000,
    },
  ];

  const skinTypes = ["دهنية", "جافة", "مختلطة", "عادية"];
  const relationshipOptions = [
    { value: "pregnant", label: "أم جديدة - في انتظار مولود", icon: "🤱" },
    { value: "nursing", label: "أم مرضعة", icon: "🍼" },
    { value: "single", label: "عازبة - أركز على نفسي", icon: "💃" },
    { value: "married", label: "متزوجة - مرحلة مستقرة", icon: "💍" },
  ];

  const sensitivityOptions = [
    "احمرار / توهج",
    "جفاف وشد",
    "حرقة / لسعة",
    "حكة",
    "ظهور حبوب / التهاب حب الشباب",
    "تقشر",
    "تفاعلات حساسية",
    "حساسية من الشمس",
    "تصبغات بعد التهيج",
    "نوبات الوردية",
  ];

  const morningRoutine = [
    {
      step: "منظف لطيف",
      product: "CeraVe Gentle Cleanser",
      image: "🧴",
      price: 85,
      originalPrice: 120,
    },
    {
      step: "تونر مرطب",
      product: "Pixi Glow Tonic",
      image: "💧",
      price: 75,
      originalPrice: 100,
    },
    {
      step: "سيروم فيتامين سي",
      product: "The Ordinary Vitamin C",
      image: "🍊",
      price: 65,
      originalPrice: 90,
    },
    {
      step: "كريم مرطب",
      product: "Neutrogena Hydrating",
      image: "🫧",
      price: 95,
      originalPrice: 130,
    },
    {
      step: "واقي الشمس",
      product: "La Roche Posay SPF 50",
      image: "☀️",
      price: 110,
      originalPrice: 150,
    },
  ];

  const eveningRoutine = [
    {
      step: "منظف",
      product: "CeraVe Gentle Cleanser",
      image: "🧴",
      price: 85,
      originalPrice: 120,
    },
    {
      step: "تونر",
      product: "Pixi Glow Tonic",
      image: "💧",
      price: 75,
      originalPrice: 100,
    },
    {
      step: "سيروم الريتينول",
      product: "The Ordinary Retinol",
      image: "🌙",
      price: 70,
      originalPrice: 95,
    },
    {
      step: "كريم ليلي",
      product: "Olay Night Recovery",
      image: "🌸",
      price: 120,
      originalPrice: 160,
    },
  ];

  // Transform AI-generated product data to match current UI structure (Skin Care)
  const transformAiProductData = (aiData) => {
    try {
      if (!aiData || !aiData.checkout_page) {
        return null;
      }

      const { checkout_page } = aiData;

      // Transform morning routine
      const transformedMorning = (checkout_page.morning_routine || []).map(
        (item, index) => ({
          step: item.title || `خطوة ${item.step || index + 1}`,
          product: item.product_name || "منتج غير محدد",
          image: item.image?.url || "🫧", // Use CDN image or fallback emoji
          imageAlt: item.image?.alt || item.product_name || "",
          price: item.price_iqd || 0,
          link: item.link || "",
          isAiGenerated: true,
        })
      );

      // Transform evening routine
      const transformedEvening = (checkout_page.evening_routine || []).map(
        (item, index) => ({
          step: item.title || `خطوة ${item.step || index + 1}`,
          product: item.product_name || "منتج غير محدد",
          image: item.image?.url || "🌙", // Use CDN image or fallback emoji
          imageAlt: item.image?.alt || item.product_name || "",
          price: item.price_iqd || 0,
          link: item.link || "",
          isAiGenerated: true,
        })
      );

      return {
        morning: transformedMorning,
        evening: transformedEvening,
        totalPrice: checkout_page.total_price_iqd || 0,
        isAiGenerated: true,
      };
    } catch (error) {
      console.error("Error transforming AI product data:", error);
      return null;
    }
  };

  // Transform hair routine product data (Hair Care)
  const transformHairProductData = (hairData) => {
    try {
      if (!hairData) return null;

      // Handle both formats: direct or inside checkout_page
      const sourceData = hairData.checkout_page || hairData;

      // Transform pre-shower routine
      const preShower = (sourceData.pre_shower_routine || []).map((item, index) => ({
        step: item.step || index + 1,
        slot: item.title || item.slot || `خطوة ${index + 1}`,
        product: item.product_name || item.product || "منتج غير محدد",
        sku: item.sku || "",
        image: item.image?.url || "🛢️",
        price: item.price_iqd || 0,
        link: item.link || "",
        isAiGenerated: true,
      }));

      // Transform shower routine
      const shower = (sourceData.shower_routine || []).map((item, index) => ({
        step: item.step || index + 1,
        slot: item.title || item.slot || `خطوة ${index + 1}`,
        product: item.product_name || item.product || "منتج غير محدد",
        sku: item.sku || "",
        image: item.image?.url || "🚿",
        price: item.price_iqd || 0,
        link: item.link || "",
        isAiGenerated: true,
      }));

      // Transform post-shower routine
      const postShower = (sourceData.post_shower_routine || []).map((item, index) => ({
        step: item.step || index + 1,
        slot: item.title || item.slot || `خطوة ${index + 1}`,
        product: item.product_name || item.product || "منتج غير محدد",
        sku: item.sku || "",
        image: item.image?.url || "✨",
        price: item.price_iqd || 0,
        link: item.link || "",
        isAiGenerated: true,
      }));

      // Transform weekly treatments
      const weekly = (sourceData.weekly_treatments || []).map((item, index) => ({
        step: item.step || index + 1,
        slot: item.title || item.slot || `خطوة ${index + 1}`,
        product: item.product_name || item.product || "منتج غير محدد",
        sku: item.sku || "",
        image: item.image?.url || "🌟",
        price: item.price_iqd || 0,
        link: item.link || "",
        isAiGenerated: true,
      }));

      return {
        preShower,
        shower,
        postShower,
        weekly,
        totalPrice: sourceData.total_price_iqd || 0,
        isAiGenerated: true,
      };


    } catch (error) {
      console.error("Error transforming hair product data:", error);
      return null;
    }
  };

  // Get the routines to display (AI-generated or fallback)
  const getDisplayRoutines = () => {
    if (aiGeneratedProducts && !hasDataError) {
      return {
        morning: aiGeneratedProducts.morning,
        evening: aiGeneratedProducts.evening,
        totalPrice: aiGeneratedProducts.totalPrice,
        isAiGenerated: true,
      };
    }

    // Fallback to hardcoded data
    return {
      morning: morningRoutine,
      evening: eveningRoutine,
      totalPrice:
        morningRoutine.reduce((sum, item) => sum + item.price, 0) +
        eveningRoutine.reduce((sum, item) => sum + item.price, 0),
      isAiGenerated: false,
    };
  };

  const displayRoutines = getDisplayRoutines();

  // Calculate pricing with discount and delivery fees
  const calculatePricing = (basePrice, city, isAiGenerated) => {
    const subtotal = basePrice;
    const discountPercent = 7;
    const discountAmount = Math.round(subtotal * (discountPercent / 100));
    const afterDiscount = subtotal - discountAmount;

    // Delivery fees for orders below 50K IQD (only for AI generated products in IQD)
    let deliveryFee = 0;
    if (isAiGenerated && afterDiscount < 50000) {
      if (city === "بغداد") {
        deliveryFee = 4000;
      } else if (city === "البصرة") {
        deliveryFee = 5000;
      } else if (city && city !== "") {
        deliveryFee = 6000;
      }
    }

    const finalTotal = afterDiscount + deliveryFee;

    return {
      subtotal,
      discountPercent,
      discountAmount,
      afterDiscount,
      deliveryFee,
      finalTotal,
      showDeliveryFee: deliveryFee > 0,
    };
  };

  // Iraqi cities list
  const iraqiCities = [
    "بغداد",
    "البصرة",
    "الموصل",
    "أربيل",
    "النجف",
    "كربلاء",
    "السليمانية",
    "الديوانية",
    "الرمادي",
    "الكوت",
    "الحلة",
    "كركوك",
    "سامراء",
    "النعمانية",
    "العمارة",
    "الناصرية",
    "دهوك",
    "زاخو",
    "الطوز",
    "تكريت",
  ];

  // Countdown timer with persistence
  useEffect(() => {
    if (currentStep === 3 && countdown > 0) {
      // Store countdown state
      storeCountdownState(countdown, currentStep);

      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (currentStep === 3 && countdown === 0 && !showPharmacistMessage) {
      // Clear countdown state when completed
      clearCountdownState();
      setShowPharmacistMessage(true);
    }
  }, [currentStep, countdown, showPharmacistMessage]);

  // AI Analysis Steps Handler
  useEffect(() => {
    if (analyzing && aiStep < aiSteps.length) {
      const currentStepData = aiSteps[aiStep];

      // Update progress gradually during each step
      const progressInterval = setInterval(() => {
        setAiProgress((prev) => {
          const stepProgress = (aiStep / aiSteps.length) * 100;
          const currentStepProgress = (1 / aiSteps.length) * 100;
          return Math.min(
            stepProgress + currentStepProgress,
            ((aiStep + 1) / aiSteps.length) * 100
          );
        });
      }, currentStepData.duration / 10);

      const stepTimeout = setTimeout(() => {
        clearInterval(progressInterval);
        if (aiStep < aiSteps.length - 1) {
          setAiStep((prev) => prev + 1);
        } else {
          setAnalyzing(false);
          setShowRoutine(true);
          setAiStep(0);
          setAiProgress(0);
        }
      }, currentStepData.duration);

      return () => {
        clearTimeout(stepTimeout);
        clearInterval(progressInterval);
      };
    }
  }, [analyzing, aiStep, aiSteps]);

  // Real-time timer for polling
  useEffect(() => {
    if (realTimeAnalyzing && pollingStartTime) {
      const timerInterval = setInterval(() => {
        setTimeElapsed(Date.now() - pollingStartTime);
      }, 1000); // Update every second

      return () => clearInterval(timerInterval);
    }
  }, [realTimeAnalyzing, pollingStartTime]);

  // Joke rotation for Phase 1 (dermatologist analysis)
  useEffect(() => {
    if (realTimeAnalyzing && pollingPhase === 1) {
      const jokeInterval = setInterval(() => {
        setCurrentJokeIndex((prev) => (prev + 1) % aiSteps.length);
      }, 4000); // Change joke every 4 seconds

      return () => clearInterval(jokeInterval);
    }
  }, [realTimeAnalyzing, pollingPhase, aiSteps.length]);

  // Modified step navigation handlers
  const handleStep1ToStep2 = async () => {
    // Step 1 to 2: Only require image upload and successful upload
    if (!uploadedImage || !uploadSuccess || creatingUser) {
      return;
    }
    setCurrentStep(2);
  };

  const handleStep2ToStep3 = async () => {
    // console.log("🔍 handleStep2ToStep3 called with formData:", formData);
    // console.log("🔍 selectedBudget:", selectedBudget);
    // console.log("🔍 selectedCountry:", selectedCountry);

    // Validate phone number with country code
    const fullPhoneNumber = `+${getCountryCallingCode(selectedCountry)}${formData.phone
      }`;
    const isPhoneValid =
      formData.phone && isValidPhoneNumber(fullPhoneNumber, selectedCountry);

    // Step 2 to 3: Validate ALL form data and save everything (including image upload)
    const isFormValid =
      formData.name &&
      isPhoneValid &&
      formData.skinType &&
      formData.relationshipStatus &&
      selectedBudget &&
      uploadSuccess && // CRITICAL: Must have uploaded image
      (formData.notSensitive ||
        formData.skinSensitivity.length > 0 ||
        formData.additionalSensitivity?.trim());

    if (!isFormValid) {
      console.log("⚠️ Form validation failed:", {
        name: !!formData.name,
        phone: !!formData.phone,
        fullPhoneNumber: fullPhoneNumber,
        phoneValid: isPhoneValid,
        skinType: !!formData.skinType,
        relationshipStatus: !!formData.relationshipStatus,
        budget: !!selectedBudget,
        uploadSuccess: uploadSuccess, // Check image upload status
        notSensitive: formData.notSensitive,
        skinSensitivityLength: formData.skinSensitivity.length,
        additionalSensitivity: formData.additionalSensitivity?.trim(),
        sensitivityCheck:
          formData.notSensitive ||
          formData.skinSensitivity.length > 0 ||
          !!formData.additionalSensitivity?.trim(),
      });

      if (!uploadSuccess) {
        alert("يرجى رفع صورة بشرتك أولاً في الخطوة الأولى");
        setCurrentStep(1); // Force user back to step 1 to upload image
      } else if (!isPhoneValid) {
        alert("يرجى إدخال رقم هاتف صالح");
      } else {
        alert("يرجى ملء جميع الحقول المطلوبة");
      }
      return;
    }

    console.log("✅ Form validation passed, proceeding with submission");

    // Set loading state
    setSubmittingForm(true);

    try {
      // Clear any previous errors
      clearError();

      // STEP 1: Generate unique submission ID for idempotency
      const submissionId = generateSubmissionId();
      console.log("🆔 Generated submission_id:", submissionId);

      // STEP 2: Check idempotency - has this submission already been processed?
      const idempotencyCheck =
        await ConsultationService.checkSubmissionIdempotency(submissionId);
      if (idempotencyCheck.exists) {
        console.log(
          "⚠️ Duplicate submission detected, navigating to success page"
        );
        setShowCompletionPage(true);
        setCurrentStep(3);
        storeCurrentStep(3);
        setSubmittingForm(false);
        return;
      }

      // STEP 3: Save to DB FIRST (with retry) - Include full phone number with country code
      const fullPhoneForSave = `+${getCountryCallingCode(selectedCountry)}${formData.phone
        }`;
      const dataToSave = {
        ...formData,
        phone: fullPhoneForSave,
      };

      let result = await saveCompleteConsultation(
        dataToSave,
        false,
        channelParams
      );

      if (!result.success) {
        console.warn(
          "⚠️ First save attempt failed, retrying once...",
          result.error
        );
        // Immediate retry once
        result = await saveCompleteConsultation(
          dataToSave,
          false,
          channelParams
        );

        if (!result.success) {
          // STOP - don't proceed or trigger webhook
          console.error(
            "❌ Second save attempt failed, blocking user progression"
          );
          alert(
            "عذراً، لا نستطيع حفظ بياناتك حالياً. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى."
          );
          setSubmittingForm(false);
          return; // Exit - user can retry button
        }

        console.log("✅ Retry successful, proceeding with submission");
      }

      const userId = result.data.user.id;
      const consultationId = result.data.consultation.id;

      // STEP 4: Store submission ID in database (marks this consultation as "being processed")
      const storeResult = await ConsultationService.storeSubmissionId(
        userId,
        submissionId
      );
      if (!storeResult.success) {
        console.warn(
          "⚠️ Failed to store submission_id, but continuing:",
          storeResult.error
        );
      }

      // STEP 5: IMMEDIATELY navigate to success page
      setShowCompletionPage(true);
      setCurrentStep(3);
      storeCurrentStep(3);
      setSubmittingForm(false);
      console.log("✅ Data saved - navigating to success page immediately");

      // STEP 6: Call webhook in background with retry logic and toast notifications
      const webhookData = {
        userId: userId,
        consultationId: consultationId,
        formData: formData,
        hasImage: uploadSuccess ? true : false,
        timestamp: new Date().toISOString(),
        budgetSelection: selectedBudget
          ? {
            range_id: selectedBudget.id,
            min_amount: selectedBudget.min_amount,
            max_amount: selectedBudget.max_amount,
            label_ar: selectedBudget.label_ar,
            label_en: selectedBudget.label_en,
          }
          : null,
        channelParams: channelParams,
      };

      // Show toast notification
      setToastStatus("pending");
      setToastMessage("جاري حفظ بياناتك...");

      // Call webhook in background (non-blocking)
      callWebhookInBackground(userId, webhookData, submissionId);
    } catch (error) {
      console.error("❌ Error in handleStep2ToStep3:", error);
      alert("حدث خطأ في إرسال البيانات. يرجى المحاولة مرة أخرى.");
      setSubmittingForm(false);
    }
  };

  // Background webhook caller with retry logic
  const callWebhookInBackground = async (userId, webhookData, submissionId) => {
    try {
      const webhookUrl = process.env.REACT_APP_HELP_WEBHOOK_URL;

      if (!webhookUrl) {
        console.warn("⚠️ No webhook URL configured, skipping webhook call");
        setToastStatus(null); // Hide toast
        return;
      }

      // Prepare payload in the EXACT format expected by n8n (with underscores)
      const webhookPayload = {
        event_type: "consultation_started",
        action: "consultation_started",
        user_choice: "start_consultation",
        button_text: "ابدئي الاستشارة ✨",
        user_id: userId, // Use user_id with underscore
        consultation_id: webhookData.consultationId, // Use consultation_id with underscore
        session_data: {
          user_info: webhookData.formData,
          has_uploaded_image: webhookData.hasImage,
          consultation_step: "consultation_start",
        },
        budget_selection: webhookData.budgetSelection,
        channel_mapping: {
          contact_id: webhookData.channelParams?.contact_id || null,
          channel_id: webhookData.channelParams?.channel_id || null,
          has_channel_info: !!(
            webhookData.channelParams?.contact_id &&
            webhookData.channelParams?.channel_id
          ),
        },
        metadata: {
          user_agent: navigator.userAgent,
          timestamp: webhookData.timestamp,
          page_url: window.location.href,
          db_save_confirmed: true,
          submission_id: submissionId, // Add submission_id to metadata
        },
      };

      // Call webhook with retry logic
      const result = await WebhookService.callWebhookWithRetry(
        webhookUrl,
        webhookPayload,
        {
          maxRetries: 3,
          submissionId: submissionId,
          onStatusChange: (status, info) => {
            if (status === "attempting") {
              setToastMessage(
                `جاري المحاولة ${info.attempt}/${info.maxRetries}...`
              );
              ConsultationService.updateWebhookStatus(
                userId,
                "pending",
                info.attempt
              );
            } else if (status === "retrying") {
              setToastMessage(`إعادة المحاولة بعد ${info.delay} ثانية...`);
            }
          },
        }
      );

      if (result.success) {
        console.log(
          "✅ Webhook sent successfully after",
          result.attempts,
          "attempt(s)"
        );
        setToastStatus("success");
        setToastMessage("تم حفظ البيانات بنجاح ✅");
        await ConsultationService.updateWebhookStatus(
          userId,
          "sent",
          result.attempts
        );

        // Auto-hide success toast after 3 seconds
        setTimeout(() => setToastStatus(null), 3000);
      } else {
        console.error("❌ Webhook failed after all retries:", result.error);
        setToastStatus("failed");
        setToastMessage("فشل الحفظ، يرجى المحاولة مرة أخرى");
        await ConsultationService.updateWebhookStatus(
          userId,
          "failed",
          result.attempts
        );

        // Store retry callback
        setWebhookRetryCallback(
          () => () => callWebhookInBackground(userId, webhookData, submissionId)
        );
      }
    } catch (error) {
      console.error("❌ Exception in callWebhookInBackground:", error);
      setToastStatus("failed");
      setToastMessage("حدث خطأ، يرجى المحاولة مرة أخرى");

      // Store retry callback
      setWebhookRetryCallback(
        () => () => callWebhookInBackground(userId, webhookData, submissionId)
      );
    }
  };

  const callImageWebhookInBackground = async (webhookData) => {
    try {
      if (!webhookData?.hasImage) {
        console.warn(
          "⚠️ [WonderBeautiesConsultation] Skipping image webhook: hasImage=false"
        );
        return;
      }

      const result = await WebhookService.callImageUploadWebhook(webhookData, {
        maxRetries: 1,
      });

      if (result.success) {
        console.log("✅ [WonderBeautiesConsultation] Image webhook sent");
      } else {
        console.error(
          "❌ [WonderBeautiesConsultation] Image webhook failed:",
          result.error
        );
      }
    } catch (error) {
      console.error(
        "❌ [WonderBeautiesConsultation] Image webhook exception:",
        error
      );
    }
  };

  // Modified image upload handler - now session-based
  const handleImageUpload = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (file) {
      // Reset states
      setImageUploading(true);
      setUploadSuccess(false);

      // Set local state immediately for UI feedback
      const imageUrl = URL.createObjectURL(file);
      setUploadedImage(imageUrl);

      // Clear any previous errors
      clearError();

      try {
        // Upload to Supabase immediately with session_id (no consultation required)
        // Pass channel params to set is_anonymous correctly
        const result = await uploadSessionImage(file, channelParams);

        if (result.success) {
          console.log("✅ Image uploaded successfully to storage");
          setCreatingUser(true);

          const uploadedUserId =
            result.data?.user_id ||
            (await ConsultationService.getCurrentUser(channelParams)).data?.id;

          if (!uploadedUserId) {
            throw new Error("Missing user_id after image upload");
          }

          const preparationResult =
            await ConsultationService.prepareImageValidationForUser(
              uploadedUserId
            );

          if (preparationResult.error || !preparationResult.data?.consultation) {
            throw (
              preparationResult.error ||
              new Error("Failed to prepare image validation")
            );
          }

          const imageWebhookData = {
            userId: uploadedUserId,
            consultationId: preparationResult.data.consultation.id,
            imageValidationRequestId:
              preparationResult.data.imageValidationRequestId,
            formData: {},
            hasImage: true,
            timestamp: new Date().toISOString(),
            channelParams,
          };

          setUploadSuccess(true);
          setStoredUploadSuccess(true); // Store success state for state restoration
          callImageWebhookInBackground(imageWebhookData);
        } else {
          console.warn(
            "⚠️ Failed to upload image to database - user will be blocked from proceeding"
          );
          setErrorMessage(
            ConsultationService.getUploadErrorMessage(result.error)
          );
          setUploadSuccess(false);
          setStoredUploadSuccess(false); // Store failure state for state restoration
        }
      } catch (error) {
        console.error("Error during image upload:", error);
        setErrorMessage(ConsultationService.getUploadErrorMessage(error));
        setUploadSuccess(false);
        setStoredUploadSuccess(false);
      } finally {
        setCreatingUser(false);
        setImageUploading(false);
      }
    }
  };

  const openImagePicker = () => {
    if (imageUploading) return;

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
      imageInputRef.current.click();
    }
  };

  const handleSensitivityToggle = (sensitivity) => {
    setFormData((prev) => ({
      ...prev,
      skinSensitivity: prev.skinSensitivity.includes(sensitivity)
        ? prev.skinSensitivity.filter((s) => s !== sensitivity)
        : [...prev.skinSensitivity, sensitivity],
      notSensitive: false,
    }));
  };

  // Real-time polling handler for AI Accept button
  const handleAIAccept = async () => {
    console.log("🚀 Starting real-time analysis polling...");

    // Hide the AI upsell section with smooth transition
    setShowAiUpsell(false);

    // Reset states
    setRealTimeAnalyzing(true);
    setAnalyzing(false); // Disable fake timer
    setPollingPhase(1);
    setDermatologistResults(null);
    setTimeElapsed(0);
    setPollingStartTime(Date.now());
    setShowTimeout(false);
    setPollingError(null);
    setCurrentJokeIndex(0);

    try {
      // Get current user ID (user should already exist at this point)
      const userResult = await ConsultationService.getCurrentUser(
        channelParams
      );
      if (userResult.error || !userResult.data) {
        console.error("❌ No user found for polling");
        setPollingError("خطأ في تحديد المستخدم");
        setRealTimeAnalyzing(false);
        return;
      }

      const userId = userResult.data.id;
      console.log("👤 Starting polling for user:", userId);

      // Use individual polling methods for better phase control
      // console.log("🔍 Phase 1: Waiting for dermatologist analysis...");

      // Phase 1: Poll for dermatologist analysis
      const dermaResult =
        await ConsultationService.pollForDermatologistAnalysis(userId, 180000);

      if (dermaResult.timeout) {
        console.log("⏰ Dermatologist analysis timeout");
        setShowTimeout(true);
        setRealTimeAnalyzing(false);
        return;
      }

      if (dermaResult.error) {
        console.error("❌ Dermatologist polling error:", dermaResult.error);
        setPollingError("حدث خطأ في التحليل الطبي");
        setRealTimeAnalyzing(false);
        return;
      }

      if (dermaResult.data) {
        console.log("✅ Dermatologist analysis completed!");

        // Store dermatologist results and switch to Phase 2
        setDermatologistResults(dermaResult.data);
        setPollingPhase(2);

        console.log("🛍️ Phase 2: Searching for products...");

        // Phase 2: Poll for products with remaining timeout
        const remainingTime = 180000 - dermaResult.timeElapsed;
        const productsResult = await ConsultationService.pollForProductsOffered(
          userId,
          remainingTime
        );

        if (productsResult.timeout) {
          console.log("⏰ Products search timeout");
          setShowTimeout(true);
          setRealTimeAnalyzing(false);
          return;
        }

        if (productsResult.error) {
          console.error("❌ Products polling error:", productsResult.error);
          setPollingError("حدث خطأ في البحث عن المنتجات");
          setRealTimeAnalyzing(false);
          return;
        }

        if (productsResult.data) {
          console.log("✅ Products found! Analysis complete.");

          // Check consultation type from the result
          const resultType = productsResult.data?.consultation_type || 'Skin Care';
          setConsultationType(resultType);

          if (resultType === 'Hair Care') {
            // Transform hair routine data
            const transformedHairData = transformHairProductData(
              productsResult.data.products_offered
            );
            if (transformedHairData) {
              setHairRoutineData(transformedHairData);
              setHasDataError(false);
            } else {
              setHasDataError(true);
            }
          } else {
            // Transform and set AI products for skin
            const transformedProducts = transformAiProductData(
              productsResult.data
            );
            if (transformedProducts) {
              setAiGeneratedProducts(transformedProducts);
              setHasDataError(false);
            } else {
              setHasDataError(true);
            }
          }

          // Show final results
          setRealTimeAnalyzing(false);
          setShowPharmacistMessage(true);
          setShowRoutine(true);

          const totalTime =
            dermaResult.timeElapsed + productsResult.timeElapsed;
          console.log(
            "🎉 Total analysis time:",
            (totalTime / 1000).toFixed(1),
            "seconds"
          );
        }
      }
    } catch (error) {
      console.error("❌ Unexpected error in handleAIAccept:", error);
      setPollingError("حدث خطأ غير متوقع");
      setRealTimeAnalyzing(false);
    }
  };

  const handleClaimGift = () => {
    setChatConfig({
      startOpen: true,
      initialMessage: "هلا وندورا 🌹، وين هديتي المجانية مالتي؟ 🎁",
    });
  };

  // New handler for the "No" button
  const handleAIDecline = async () => {
    // Prepare consultation data for webhook
    const webhookData = {
      userId: consultationData?.user?.id || "anonymous",
      consultationId: consultationData?.consultation?.id || null,
      formData: formData,
      hasImage: uploadedImage ? true : false,
      timestamp: new Date().toISOString(),
      channelParams: channelParams, // Include channel mapping for multi-channel support
    };

    // Call webhook
    const webhookResult = await WebhookService.callDeclineWebhook(webhookData);

    if (!webhookResult.success) {
      console.warn(
        "Webhook call failed, but continuing with app flow:",
        webhookResult.error
      );
    } else {
      console.log("✅ Decline webhook called successfully");
    }

    // Handle the decline - show a message
    alert("شكراً لك! يمكنك العودة في أي وقت للحصول على المساعدة");
  };

  const handleOrder = () => {
    setShowAddressPopup(true);
  };

  // Handle sharing functionality
  const handleShare = async (method) => {
    const shareText =
      "🌟 اكتشفت شيء رائع! أنصحكم بـ Wonder Beauties للعناية بالبشرة - تحليل شخصي ومنتجات مخصصة! ✨";
    const shareUrl = window.location.origin;

    if (method === "copy") {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        alert("تم نسخ الرابط! 📋");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = `${shareText}\n${shareUrl}`;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert("تم نسخ الرابط! 📋");
      }
    } else if (method === "whatsapp") {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
        `${shareText}\n${shareUrl}`
      )}`;
      window.open(whatsappUrl, "_blank");
    } else if (method === "native") {
      if (navigator.share) {
        try {
          await navigator.share({
            title: "Wonder Beauties",
            text: shareText,
            url: shareUrl,
          });
        } catch (err) {
          console.log("Error sharing:", err);
        }
      }
    }
  };

  // Handle contact support - redirects to appropriate platform based on channel
  const handleContactSupport = () => {
    const channelId = channelParams.channel_id;

    // Instagram: channel_id = 377656
    if (channelId === "377656") {
      const instagramUrl = "https://www.instagram.com/wonder.beauties.iraq/";
      window.open(instagramUrl, "_blank");
    }
    // Facebook: channel_id = 197324
    else if (channelId === "197324") {
      const facebookUrl = "https://www.facebook.com/wonder.beauties.iraq/";
      window.open(facebookUrl, "_blank");
    }
    // WhatsApp (channel_id = 156631) or general link (no channel_id) - default to WhatsApp
    else {
      const phoneNumber = "9647766870115";
      const message = encodeURIComponent("اريد الاستشارة وكود خصم وندورة");
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      window.open(whatsappUrl, "_blank");
    }
  };

  // Handle start new consultation - clears localStorage and resets to step 1
  const handleStartNewConsultation = () => {
    console.log("🔄 Starting new consultation session...");

    // Clear localStorage and get new session ID
    const newSessionId = startNewConsultationSession();

    // Reset all component state to initial values
    setCurrentStep(1);
    setShowCompletionPage(false);
    setFormData({
      name: "",
      phone: "",
      skinType: "",
      relationshipStatus: "",
      skinSensitivity: [],
      notSensitive: false,
      additionalSensitivity: "",
    });
    setUploadedImage(null);
    setShowRoutine(false);
    setAiGeneratedProducts(null);
    setShowPharmacistMessage(false);
    setAnalyzing(false);
    setAiStep(0);
    setAiProgress(0);
    setOrderSuccess(false);
    setShowSkinReport(false);
    setShowSkinReportPreview(false);
    setHasDataError(false);
    setShowAddressPopup(false);
    setOrderData({
      address: "",
      city: "بغداد",
    });
    setPollingPhase(1);
    setDermatologistResults(null);
    setTimeElapsed(0);
    setPollingStartTime(null);
    setShowTimeout(false);
    setRealTimeAnalyzing(false);
    setCurrentJokeIndex(0);
    setPollingError(null);
    setShowChat(false);
    setChatPromptShown({
      topOfPage: false,
      endOfProducts: false,
      userInteracted: false,
    });
    setShowAiUpsell(true);
    setSelectedBudget(null);
    setUploadSuccess(false);
    setImageUploading(false);
    setCreatingUser(false);
    setSubmittingForm(false);
    setRefreshingProducts(false);
    setRefreshError(null);
    setRefreshSuccess(false);
    setCountdown(10);

    // Store the reset step
    storeCurrentStep(1);

    console.log("✅ New consultation session ready:", newSessionId);
  };

  // Simple refresh products function - NO AUTO-CHECK BULLSHIT
  const refreshProducts = async () => {
    const userId = getStoredUserId();

    // Debug info
    // console.log("🔍 Debug info:", {
    //   storedUserId: userId,
    //   consultationDataUserId: consultationData?.user?.id,
    //   hasConsultationData: !!consultationData,
    //   consultationDataKeys: consultationData
    //     ? Object.keys(consultationData)
    //     : "none",
    // });

    if (!userId) {
      console.log(
        "⚠️ No user ID found in localStorage, trying to get/create user..."
      );
      try {
        const userResult = await ConsultationService.getCurrentUser(
          channelParams
        );
        if (userResult.data?.id) {
          console.log("✅ User retrieved/created:", userResult.data.id);
          // Continue with the new user ID
          const newUserId = userResult.data.id;
          await continueRefresh(newUserId);
          return;
        } else {
          setRefreshError("فشل في إنشاء المستخدم. يرجى إعادة تحميل الصفحة.");
          return;
        }
      } catch (error) {
        console.error("❌ Error getting/creating user:", error);
        setRefreshError("خطأ في النظام. يرجى إعادة تحميل الصفحة.");
        return;
      }
    }

    // Main refresh logic - use existing user ID
    await continueRefresh(userId);
  };

  // Separate function for the actual refresh logic
  const continueRefresh = async (userIdToUse) => {
    setRefreshingProducts(true);
    setRefreshError(null);
    setRefreshSuccess(false);
    console.log("🔄 Refreshing products for user:", userIdToUse);

    try {
      // Get fresh consultation data
      const result = await getFreshConsultationData(userIdToUse);
      console.log("📥 Fresh data result:", result);

      // console.log("🔍 Detailed data structure:", {
      //   resultSuccess: result.success,
      //   hasData: !!result.data,
      //   hasConsultation: !!result.data?.consultation,
      //   hasProductsOffered: !!result.data?.consultation?.products_offered,
      //   productsOfferedKeys: result.data?.consultation?.products_offered
      //     ? Object.keys(result.data.consultation.products_offered)
      //     : "none",
      //   fullConsultation: result.data?.consultation,
      // });

      if (result.success && result.data?.consultation?.products_offered) {
        console.log(
          "✅ Products offered found:",
          result.data.consultation.products_offered
        );

        // Get consultation type
        const refreshedType = result.data.consultation?.consultation_type || 'Skin Care';
        setConsultationType(refreshedType);

        if (refreshedType === 'Hair Care') {
          // Transform hair routine data
          const transformedHairData = transformHairProductData(
            result.data.consultation.products_offered
          );
          console.log("🔄 Transformed hair data:", transformedHairData);

          if (transformedHairData) {
            setHairRoutineData(transformedHairData);
            setHasDataError(false);

            // Update localStorage cache
            const currentAppState = getStoredAppState();
            const updatedAppState = {
              ...currentAppState,
              hairRoutineData: transformedHairData,
              showRoutine: true,
              showPharmacistMessage: true,
            };
            storeAppState(updatedAppState);

            // Store timestamp
            storeLastProductUpdate(result.data.consultation.updated_at);

            console.log("✅ Hair products refreshed successfully");
            setRefreshSuccess(true);
          } else {
            console.error("❌ Failed to transform hair product data");
            setRefreshError("فشل في معالجة بيانات منتجات الشعر");
          }
        } else {
          // Transform skin routine data
          const transformedProducts = transformAiProductData(
            result.data.consultation.products_offered
          );
          console.log("🔄 Transformed products:", transformedProducts);

          if (transformedProducts) {
            // Update product state
            setAiGeneratedProducts(transformedProducts);
            setHasDataError(false);

            // Update localStorage cache
            const currentAppState = getStoredAppState();
            const updatedAppState = {
              ...currentAppState,
              aiGeneratedProducts: transformedProducts,
              showRoutine: true,
              showPharmacistMessage: true,
            };
            storeAppState(updatedAppState);

            // Store timestamp
            storeLastProductUpdate(result.data.consultation.updated_at);

            console.log("✅ Products refreshed successfully");
            setRefreshSuccess(true);
          } else {
            console.error("❌ Failed to transform product data");
            setRefreshError("فشل في معالجة بيانات المنتجات");
          }
        }
      } else {
        console.log("❌ No products found. Reasons:");
        console.log("   - Result success:", result.success);
        console.log("   - Has data:", !!result.data);
        console.log("   - Has consultation:", !!result.data?.consultation);
        console.log(
          "   - Has products_offered:",
          !!result.data?.consultation?.products_offered
        );
        console.log("   - Result error:", result.error);
        setRefreshError("لا توجد منتجات متاحة");
      }
    } catch (error) {
      console.error("❌ Error refreshing products:", error);
      setRefreshError("خطأ في الاتصال. يرجى المحاولة مرة أخرى.");
    } finally {
      setRefreshingProducts(false);
    }
  };

  const handleAddressSubmit = async () => {
    if (!orderData.address.trim()) {
      alert("يرجى إدخال العنوان");
      return;
    }

    try {
      // Calculate final pricing with discount and delivery fees
      const pricing = calculatePricing(
        displayRoutines.totalPrice,
        orderData.city,
        displayRoutines.isAiGenerated
      );

      // Insert order into database
      const orderResult = await ConsultationService.createOrder({
        address: orderData.address,
        city: orderData.city,
        products: displayRoutines,
        total: pricing.finalTotal,
        originalTotal: pricing.subtotal,
        discount: pricing.discountAmount,
        deliveryFee: pricing.deliveryFee,
        currency: displayRoutines.isAiGenerated ? "د.ع" : "SAR",
        formData,
      });

      if (orderResult.error) {
        console.error("Order creation failed:", orderResult.error);
        alert("حدث خطأ في تسجيل الطلب. يرجى المحاولة مرة أخرى.");
        return;
      }

      console.log("✅ Order created successfully:", orderResult.data);
      setShowAddressPopup(false);
      setOrderSuccess(true);

      // Navigate to completion page after a short delay
      setTimeout(() => {
        setOrderSuccess(false);
        setShowCompletionPage(true);
      }, 3000);
    } catch (error) {
      console.error("Error placing order:", error);
      alert("حدث خطأ في تسجيل الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  // Completion Page Component
  const CompletionPage = ({ onShare, onContactSupport, onStartNew }) => {
    const channelId = channelParams.channel_id;

    // Determine platform name and message based on channel_id
    const getPlatformInfo = () => {
      if (channelId === "377656") {
        return {
          platform: "Instagram",
          platformArabic: "إنستغرام",
          emoji: "📷",
          message: "ارسلي كلمة وندورة على إنستغرام حتى تحصلي على كود الخصم",
        };
      } else if (channelId === "197324") {
        return {
          platform: "Facebook",
          platformArabic: "فيسبوك",
          emoji: "👍",
          message: "ارسلي كلمة وندورة على فيسبوك حتى تحصلي على كود الخصم",
        };
      } else {
        // WhatsApp or general link (default)
        return {
          platform: "WhatsApp",
          platformArabic: "واتساب",
          emoji: "💬",
          message:
            "تواصلي معنا الآن عبر الواتساب واحصلي على كود خصم خاص لطلبك!",
        };
      }
    };

    const platformInfo = getPlatformInfo();

    return (
      <div
        className="min-h-screen bg-[#FFF5F7] w-full overflow-y-auto"
        dir="rtl"
        translate="no"
      >
        <div className="max-w-md mx-auto px-4 py-6 pb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
            {/* Conditional rendering based on flow type */}
            {!ENABLE_FULL_FLOW ? (
              /* SIMPLIFIED FLOW: Thank You Message */
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    شكراً لك!
                  </h2>
                  <p className="text-gray-600">تم استلام معلوماتك بنجاح</p>
                </div>

                <div className="space-y-4 mb-6 text-center">
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <Clock className="w-6 h-6 text-[#A73A5D]" />
                      <h3 className="text-xl font-bold text-gray-900">
                        10 دقائق
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      إحدى صيدلانياتنا المتخصصات ستتواصل معك خلال 10 دقائق
                      لمساعدتك
                    </p>
                  </div>

                  {/* Discount CTA Section - Platform Aware */}
                  <div className="bg-gradient-to-br from-[#A73A5D] to-[#8F2F4D] rounded-2xl p-6 shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                    <div className="flex items-center justify-center gap-2 mb-5">
                      <Gift className="w-7 h-7 text-white animate-pulse" />
                    </div>
                    <p className="text-white/90 leading-relaxed text-base mb-5 text-center font-medium">
                      {platformInfo.message} 🎀✨
                    </p>
                    <button
                      onClick={onContactSupport}
                      className="w-full bg-white text-[#A73A5D] py-4 px-6 rounded-xl font-bold text-lg shadow-md hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center justify-center gap-3"
                    >
                      <span className="text-xl">{platformInfo.emoji}</span>
                      <span>اضغطي هنا {platformInfo.platformArabic} ✨</span>
                    </button>
                  </div>
                </div>

                {/* Contact Info Display */}
                <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-center">
                    معلوماتك المسجلة:
                  </h4>
                  <div className="space-y-2 text-gray-700">
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium">الاسم:</span>
                      <span className="text-right break-words flex-1 ml-2">
                        {formData.name}
                      </span>
                    </p>
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium">الهاتف:</span>
                      <span dir="ltr" className="text-left">
                        {formData.phone}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Start New Consultation - COMMENTED OUT: Customers were misusing this feature */}
                {/* {onStartNew && (
                <div className="border-t border-gray-200 pt-5 mt-5">
                  <button
                    onClick={onStartNew}
                    className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 p-4 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
                  >
                    <RefreshCw className="w-5 h-5" />
                    ابدئي استشارة جديدة
                  </button>
                </div>
              )} */}
              </>
            ) : (
              /* FULL FLOW: Original Order Success Message */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-10 h-10 text-white animate-bounce" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#2D1B1F] mb-2">
                    ✨ تمت العملية بنجاح!
                  </h2>
                </div>

                {/* Beautiful Message */}
                <div className="space-y-4 mb-8 text-right leading-relaxed">
                  <p className="text-gray-700 text-sm">
                    <span className="font-bold text-pink-600">
                      ✨ الفريق التقني كمل الشغل،
                    </span>{" "}
                    وكانوا ينتظرون بس تدوسين "اطلبي الآن"{" "}
                    <span className="text-xl">👆</span>
                  </p>

                  <p className="text-gray-700 text-sm">
                    <span className="font-bold text-green-600">
                      🚀 وهسه ديسرعون
                    </span>{" "}
                    حتى يخدمون طلبچ (وصل الدم للركب منشان وندورا{" "}
                    <span className="text-yellow-600">😅</span> وانتي من أوائل
                    المميزين اللي طلبوا).
                  </p>

                  <p className="text-gray-700 text-sm">
                    <span className="font-bold text-purple-600">
                      💜 وندورا متوصية بيچ،
                    </span>{" "}
                    وخلّت الفريق التقني يبعثولچ{" "}
                    <span className="text-xl">🎁</span> هدية حلوة إن شاء الله
                    تعجبچ (للأسف ميزانية الشركة مو مفتوحة{" "}
                    <span className="text-yellow-600">😂</span>).
                  </p>

                  <p className="text-gray-700 text-sm">
                    <span className="font-bold text-blue-600">
                      👉 إذا حبيتي تجربتچ ويانه،
                    </span>{" "}
                    شاركيها ويا أصدقائچ <span className="text-xl">🙌</span>
                  </p>
                </div>

                {/* Discount Code Section */}
                <div className="bg-gradient-to-r from-[#A73A5D]/10 to-pink-100 rounded-xl p-5 border-2 border-[#A73A5D]/30 shadow-md mb-6">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Gift className="w-6 h-6 text-[#A73A5D]" />
                    <h3 className="text-xl font-bold text-[#A73A5D]">
                      كود الخصم الخاص
                    </h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-3 border-2 border-dashed border-[#A73A5D]">
                    <p
                      className="text-3xl font-bold text-[#A73A5D] mb-2"
                      dir="rtl"
                    >
                      وندورة
                    </p>
                  </div>
                  <p className="text-gray-700 leading-relaxed font-medium text-center text-sm">
                    اسألي صيدلانيتك عن كود الخصم{" "}
                    <span className="text-[#A73A5D] font-bold">وندورة</span>{" "}
                    واحصلي على خصم خاص عند الطلب! ✨
                  </p>
                </div>

                {/* Share Options */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-bold text-rose-700 text-center flex items-center justify-center gap-2">
                    <Share2 className="w-5 h-5" />
                    شاركي التجربة مع أصدقائچ
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    {/* WhatsApp Share */}
                    <button
                      onClick={() => onShare("whatsapp")}
                      className="flex items-center justify-center gap-2 bg-green-500 text-white p-3 rounded-xl hover:bg-green-600 transition-all duration-300 hover:scale-105 shadow-lg"
                    >
                      <span className="text-xl">📱</span>
                      <span className="font-semibold text-sm">واتساب</span>
                    </button>

                    {/* Copy Link */}
                    <button
                      onClick={() => onShare("copy")}
                      className="flex items-center justify-center gap-2 bg-blue-500 text-white p-3 rounded-xl hover:bg-blue-600 transition-all duration-300 hover:scale-105 shadow-lg"
                    >
                      <Copy className="w-5 h-5" />
                      <span className="font-semibold text-sm">نسخ الرابط</span>
                    </button>
                  </div>

                  {/* Native Share (if available) */}
                  {navigator.share && (
                    <button
                      onClick={() => onShare("native")}
                      className="w-full flex items-center justify-center gap-2 bg-[#A73A5D] text-white p-3 rounded-xl hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105"
                    >
                      <Share2 className="w-5 h-5" />
                      <span className="font-semibold">شاركي بطريقة أخرى</span>
                    </button>
                  )}
                </div>

                {/* Support Contact */}
                <div className="border-t border-pink-200 pt-6">
                  <button
                    onClick={onContactSupport}
                    className="w-full flex items-center justify-center gap-2 bg-[#A73A5D] text-white p-4 rounded-xl hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105 font-bold"
                  >
                    <MessageCircle className="w-6 h-6" />
                    تحدثي مع فريق الدعم
                  </button>

                  <p className="text-center text-sm text-gray-500 mt-2">
                    أي استفسار؟ نحن هنا لمساعدتك! 💕
                  </p>
                </div>

                {/* Start New Consultation - COMMENTED OUT: Customers were misusing this feature */}
                {/* {onStartNew && (
            <div className="border-t border-pink-200 pt-6 mt-6">
              <button
                onClick={onStartNew}
                className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 p-4 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:scale-105 font-medium border border-gray-300"
              >
                <RefreshCw className="w-5 h-5" />
                ابدئي استشارة جديدة
              </button>
            </div>
          )} */}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <LoadingWrapper
      loading={initialLoading}
      message="جاري استعادة جلستك..."
      subMessage="سنعيدك إلى حيث توقفت"
    >
      <div className="min-h-screen bg-[#FFF5F7]" translate="no">
        {/* Database Error Display */}
        {dbError && (
          <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
            <div className="flex items-center">
              <span className="ml-2">{dbError}</span>
              <button
                onClick={clearError}
                className="mr-2 text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {dbLoading && (
          <div className="fixed top-4 left-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-lg shadow-lg z-50">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 ml-2"></div>
              <span>جاري الحفظ...</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white border-b border-[#A73A5D]/10 p-4 sticky top-0 z-40">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-xl font-bold text-[#A73A5D] tracking-wide leading-tight" lang="en" translate="no">
                WONDER
                <br />
                BEAUTIES
              </h1>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 flex gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className="flex-1 h-2 bg-[#A73A5D]/10 rounded-full overflow-hidden"
              >
                <div
                  className={`h-full transition-all duration-700 ease-out ${currentStep >= step ? "bg-[#A73A5D]" : "bg-transparent"
                    }`}
                  style={{
                    width: currentStep >= step ? "100%" : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Step indicator */}
          <div className="mt-2 text-center">
            <span className="text-sm text-[#A73A5D] font-medium">
              الخطوة {currentStep} من 3
            </span>
          </div>
        </div>

        <div className="p-6 max-w-md mx-auto">
          {/* Step 1: Photo Upload */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-20 h-20 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-[#2D1B1F] mb-2">
                  ارفعي صورة بشرتك
                </h2>
                <p className="text-[#6B4E58]">
                  سنحلل بشرتك لنقدم لك أفضل العناية
                </p>
                <p className="text-sm text-[#A73A5D] mt-2 font-medium">
                  صورتك آمنة و سرّية ولا يتم الاحتفاظ بها او نشرها 🙏🏻
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 border border-[#A73A5D]/10 shadow-sm">
                <input
                  ref={imageInputRef}
                  type="file"
                  id="consultation-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={imageUploading}
                />
                {!uploadedImage ? (
                  <button
                    type="button"
                    onClick={openImagePicker}
                    disabled={imageUploading}
                    className="block w-full cursor-pointer group text-right disabled:cursor-not-allowed"
                  >
                    <div className="border-2 border-dashed border-[#A73A5D]/20 rounded-2xl p-12 text-center hover:border-[#A73A5D]/30 transition-all duration-300 group-hover:bg-[#FBF8F9] group-hover:scale-105">
                      <Camera className="w-16 h-16 text-[#A73A5D] mx-auto mb-4" />
                      <p className="text-lg font-semibold text-[#2D1B1F]">
                        اضغطي لرفع الصورة
                      </p>
                      <p className="text-sm text-[#6B4E58] mt-2">
                        أو اسحبي الصورة هنا
                      </p>
                      <div className="mt-4 flex justify-center gap-2">
                        <div
                          className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce"
                          style={{ animationDelay: "200ms" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-[#A73A5D] rounded-full animate-bounce"
                          style={{ animationDelay: "400ms" }}
                        ></div>
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="text-center space-y-4 animate-fade-in">
                    <div className="relative">
                      <img
                        src={uploadedImage}
                        alt="Uploaded skin"
                        className="w-40 h-40 rounded-full mx-auto object-cover border-4 border-[#A73A5D]/20 shadow-md"
                      />
                      <div
                        className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center animate-bounce ${uploadSuccess
                          ? "bg-green-500"
                          : imageUploading
                            ? "bg-yellow-500"
                            : "bg-red-500"
                          }`}
                      >
                        {imageUploading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : uploadSuccess ? (
                          <Check className="w-5 h-5 text-white" />
                        ) : (
                          <span className="text-white text-lg">!</span>
                        )}
                      </div>
                    </div>

                    {/* Upload Status Message */}
                    <div className="flex items-center justify-center gap-2 animate-fade-in">
                      {imageUploading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span className="font-semibold text-blue-600">
                            جاري رفع الصورة...
                          </span>
                        </>
                      ) : uploadSuccess ? (
                        <>
                          <Check className="w-5 h-5 text-green-600 animate-pulse" />
                          <span className="font-semibold text-green-600">
                            تم رفع الصورة بنجاح!
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="w-5 h-5 text-red-500">⚠️</span>
                          <span className="font-semibold text-red-600">
                            فشل في رفع الصورة
                          </span>
                        </>
                      )}
                    </div>

                    <button
                      onClick={handleStep1ToStep2}
                      disabled={!uploadSuccess || creatingUser}
                      className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-200 ${uploadSuccess && !creatingUser
                        ? "bg-[#A73A5D] text-white hover:bg-[#8F2F4D] hover:shadow-md hover:scale-105"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      {imageUploading
                        ? "جاري الرفع..."
                        : creatingUser
                          ? "جاري تجهيز الصورة..."
                          : "التالي ✨"}
                    </button>

                    {/* Retry button if upload failed */}
                    {!imageUploading && !uploadSuccess && (
                      <button
                        type="button"
                        onClick={openImagePicker}
                        className="w-full bg-gradient-to-r from-blue-400 to-blue-500 text-white py-3 rounded-2xl font-bold hover:shadow-lg transition-all duration-300 hover:scale-105"
                      >
                        إعادة المحاولة 🔄
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Basic Info */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-20 h-20 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-[#2D1B1F] mb-2">
                  معلوماتك الأساسية
                </h2>
                <p className="text-[#6B4E58]">حتى نقدم لك العناية المناسبة</p>
              </div>

              <div className="bg-white rounded-3xl p-6 border border-[#A73A5D]/10 space-y-6 shadow-sm">
                {/* Name & Phone */}
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="الاسم"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full p-4 rounded-2xl border border-[#A73A5D]/15 bg-white text-right focus:ring-2 focus:ring-[#A73A5D]/20 focus:border-[#A73A5D] transition-all duration-300 hover:border-[#A73A5D]/30"
                    />
                    {formData.name && (
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <Check className="w-5 h-5 text-green-500 animate-pulse" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div
                      className={`custom-phone-container ${formData.phone &&
                        !isValidPhoneNumber(
                          `+${getCountryCallingCode(selectedCountry)}${formData.phone
                          }`,
                          selectedCountry
                        )
                        ? "border-red-500"
                        : "border-[#A73A5D]/15"
                        }`}
                    >
                      {/* Country Selector Dropdown */}
                      <div className="country-selector-wrapper">
                        <select
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          className="country-selector"
                        >
                          {getCountries()
                            .filter((country) => country !== "IL")
                            .map((country) => (
                              <option key={country} value={country}>
                                {en[country]} (+{getCountryCallingCode(country)}
                                )
                              </option>
                            ))}
                        </select>
                        <ChevronDown className="dropdown-icon" size={16} />
                      </div>

                      {/* Country Code Display */}
                      <span className="country-code-display">
                        +{getCountryCallingCode(selectedCountry)}
                      </span>

                      {/* Phone Number Input */}
                      <input
                        type="tel"
                        placeholder="رقم الهاتف"
                        value={formData.phone}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setFormData((prev) => ({ ...prev, phone: value }));
                        }}
                        className="phone-number-input"
                      />

                      {/* Validation Checkmark */}
                      {formData.phone &&
                        isValidPhoneNumber(
                          `+${getCountryCallingCode(selectedCountry)}${formData.phone
                          }`,
                          selectedCountry
                        ) && (
                          <div className="validation-check">
                            <Check className="w-5 h-5 text-green-500 animate-pulse" />
                          </div>
                        )}
                    </div>

                    {/* Error Message */}
                    {formData.phone &&
                      !isValidPhoneNumber(
                        `+${getCountryCallingCode(selectedCountry)}${formData.phone
                        }`,
                        selectedCountry
                      ) && (
                        <p className="text-red-500 text-sm mt-2 text-right animate-fade-in">
                          ⚠️ رقم الهاتف غير صحيح. يرجى التحقق من الرقم
                        </p>
                      )}
                  </div>
                </div>

                {/* Skin Type */}
                <div>
                  <h3 className="text-lg font-bold text-[#A73A5D] mb-3 flex items-center gap-2">
                    <span className="text-xl">✨</span>
                    نوع البشرة
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {skinTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, skinType: type }))
                        }
                        className={`p-3 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${formData.skinType === type
                          ? "border-[#A73A5D] bg-[#A73A5D]/10 text-[#A73A5D] shadow-sm"
                          : "border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30"
                          }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Life Stage */}
                <div>
                  <h3 className="text-lg font-bold text-[#A73A5D] mb-3 flex items-center gap-2">
                    <span className="text-xl">👩‍❤️‍👨</span>
                    مرحلتك الحياتية
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {relationshipOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            relationshipStatus: option.value,
                          }))
                        }
                        className={`p-3 rounded-xl border-2 transition-all duration-300 text-center hover:scale-105 ${formData.relationshipStatus === option.value
                          ? "border-[#A73A5D] bg-[#A73A5D]/10 text-[#A73A5D] shadow-sm"
                          : "border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30"
                          }`}
                      >
                        <div className="text-2xl mb-1 animate-bounce">
                          {option.icon}
                        </div>
                        <div className="text-xs font-semibold leading-tight">
                          {option.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skin Sensitivity */}
                <div>
                  <h3 className="text-lg font-bold text-[#A73A5D] mb-3 flex items-center gap-2">
                    <span className="text-xl">🚨</span>
                    مشاكل الحساسية *
                  </h3>

                  {/* Not Sensitive Option */}
                  <button
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        skinSensitivity: [],
                        notSensitive: !prev.notSensitive,
                        additionalSensitivity: prev.notSensitive
                          ? prev.additionalSensitivity
                          : "",
                      }))
                    }
                    className={`w-full p-3 rounded-xl border-2 transition-all duration-300 text-right mb-4 hover:scale-105 ${formData.notSensitive
                      ? "border-green-600 bg-green-50 text-green-700 shadow-sm"
                      : "border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30"
                      }`}
                  >
                    ✨ بشرتي غير حساسة
                  </button>

                  {!formData.notSensitive && (
                    <div className="space-y-4 animate-fade-in">
                      {/* Small floating chips */}
                      <div className="flex flex-wrap gap-2">
                        {sensitivityOptions.map((sensitivity) => (
                          <button
                            key={sensitivity}
                            onClick={() => handleSensitivityToggle(sensitivity)}
                            className={`px-3 py-2 rounded-full text-xs border transition-all duration-300 hover:scale-105 ${formData.skinSensitivity.includes(sensitivity)
                              ? "border-[#A73A5D] bg-[#A73A5D]/10 text-[#A73A5D] shadow-sm"
                              : "border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30"
                              }`}
                          >
                            {sensitivity}
                          </button>
                        ))}
                      </div>

                      {/* Text input bar */}
                      <input
                        type="text"
                        placeholder="أو اكتبي مشاكل إضافية للبشرة..."
                        value={formData.additionalSensitivity || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            additionalSensitivity: e.target.value,
                          }))
                        }
                        className="w-full p-4 rounded-2xl border border-[#A73A5D]/15 bg-white text-right focus:ring-2 focus:ring-[#A73A5D]/20 focus:border-[#A73A5D] transition-all duration-300 hover:border-[#A73A5D]/30"
                      />
                    </div>
                  )}
                </div>

                {/* Budget Selection */}
                <div>
                  <h3 className="text-lg font-bold text-[#A73A5D] mb-3 flex items-center gap-2">
                    <span className="text-xl">💰</span>
                    اختاري ميزانيتك *
                  </h3>
                  <p className="text-sm text-[#6B4E58] mb-4">
                    سنختار لك منتجات تناسب ميزانيتك المحددة
                  </p>

                  <div className="space-y-3">
                    {budgetRanges.map((range) => (
                      <button
                        key={range.id}
                        onClick={() => setSelectedBudget(range)}
                        className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-right hover:scale-105 ${selectedBudget?.id === range.id
                          ? "border-[#A73A5D] bg-[#A73A5D]/10 text-[#A73A5D] shadow-sm"
                          : "border-[#A73A5D]/15 bg-white text-[#6B4E58] hover:border-[#A73A5D]/30"
                          }`}
                      >
                        <span className="text-lg font-semibold">
                          {range.label_ar}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleStep2ToStep3}
                  disabled={
                    submittingForm ||
                    !formData.name ||
                    !formData.phone ||
                    !(
                      formData.phone &&
                      isValidPhoneNumber(
                        `+${getCountryCallingCode(selectedCountry)}${formData.phone
                        }`,
                        selectedCountry
                      )
                    ) ||
                    !formData.skinType ||
                    !selectedBudget ||
                    (!formData.notSensitive &&
                      formData.skinSensitivity.length === 0 &&
                      !formData.additionalSensitivity?.trim())
                  }
                  className="w-full bg-[#A73A5D] text-white py-4 rounded-2xl font-bold text-lg hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submittingForm ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      جاري الإرسال...
                    </span>
                  ) : (
                    "ابدئي الاستشارة ✨"
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Pharmacist Search & AI Analysis (FULL FLOW ONLY) */}
          {ENABLE_FULL_FLOW && currentStep === 3 && (
            <div className="space-y-6">
              {/* Countdown Phase */}
              {!showPharmacistMessage && (
                <div className="text-center bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-pink-200 shadow-xl animate-fade-in">
                  <div className="relative mb-6">
                    <Clock className="w-16 h-16 text-pink-400 mx-auto animate-spin" />
                    <div className="absolute inset-0 w-16 h-16 mx-auto border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin"></div>
                  </div>
                  <h2 className="text-2xl font-bold text-rose-800 mb-2">
                    نبحث عن صيدلانية متاحة
                  </h2>
                  <p className="text-rose-600 mb-4">يرجى الانتظار...</p>
                  <div
                    className="text-6xl font-bold text-pink-500 animate-bounce"
                    style={{ animation: "bounce 1s infinite" }}
                  >
                    {countdown}
                  </div>
                  <div className="mt-4 flex justify-center gap-1">
                    <div
                      className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                      style={{ animationDelay: "200ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                      style={{ animationDelay: "400ms" }}
                    ></div>
                  </div>
                </div>
              )}

              {/* No Pharmacist + AI Upsell */}
              {showPharmacistMessage &&
                !analyzing &&
                !showRoutine &&
                showAiUpsell && (
                  <div
                    className={`space-y-4 transition-all duration-500 ease-in-out ${showAiUpsell
                      ? "opacity-100 transform translate-y-0"
                      : "opacity-0 transform -translate-y-4 pointer-events-none"
                      }`}
                  >
                    <div className="text-center bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-pink-200 shadow-xl">
                      <div className="text-4xl mb-3 animate-bounce">😔</div>
                      <h2 className="text-xl font-bold text-rose-800 mb-1">
                        جميع صيدلانياتنا مشغولات حالياً
                      </h2>
                      <p className="text-[#6B4E58] text-sm">
                        سنتواصل معك خلال 24 ساعة 🌸
                      </p>
                    </div>

                    <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-pink-200 space-y-4 shadow-xl">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-3">
                          <Sparkles className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-[#2D1B1F] mb-2">
                          ✨ لا تنتظرين! جرّبي وندورة هسه
                        </h2>
                      </div>

                      <div className="space-y-3">
                        {[
                          "استشارة فورية بدگة زر",
                          "تجاوبچ مباشرة وتفهم بشرتچ",
                          "لا طوابير ولا انتظـار",
                          " معلوماتچ محفوظة تماماً وصورچ ما تستخدم للتدريب 🔒 ",
                        ].map((text, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-2 bg-pink-50 rounded-xl hover:bg-pink-100 transition-all duration-300 hover:scale-105"
                            style={{ animationDelay: `${index * 200}ms` }}
                          >
                            <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                            <span className="text-rose-700 text-sm">
                              {text}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleAIAccept}
                          className="flex-1 bg-[#A73A5D] text-white py-3 rounded-2xl font-bold hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105"
                        >
                          نعم، أريد المساعدة! ✨
                        </button>
                        <button
                          onClick={handleAIDecline}
                          className="px-6 py-3 border-2 border-[#A73A5D]/20 text-[#A73A5D] rounded-2xl hover:border-[#A73A5D]/30 transition-all duration-300 text-sm hover:scale-105 hover:bg-[#FBF8F9]"
                        >
                          لا :(
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              {/* REAL-TIME AI ANALYSIS - TWO PHASE UI */}
              {realTimeAnalyzing && (
                <div className="space-y-6 transition-all duration-500 ease-in-out animate-fade-in">
                  {/* Time Elapsed & Phase Indicator */}
                  <div className="bg-white rounded-2xl p-4 border border-[#A73A5D]/10 shadow-sm">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-purple-600 animate-pulse" />
                        <span className="text-purple-700 font-semibold">
                          وقت التحليل: {Math.floor(timeElapsed / 1000)} ثانية
                        </span>
                      </div>
                      <div className="text-sm text-purple-600">
                        {pollingPhase === 1
                          ? "🔍 المرحلة الأولى"
                          : "🛍️ المرحلة الثانية"}
                      </div>
                    </div>
                    <div className="mt-4 text-base text-[#A73A5D] text-center p-3 bg-[#A73A5D]/10 rounded-lg shadow-sm">
                      <strong>التحليل دقيق وقد يستغرق حتى دقيقتين.</strong>
                      <br />
                      شكراً جزيلاً لصبرك! ⏱️
                    </div>
                  </div>

                  {/* Free Gift Button */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border-2 border-dashed border-pink-300 shadow-lg mt-6 text-center transition-all duration-300 hover:shadow-xl hover:border-pink-400">
                    <div className="w-16 h-16 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Gift className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-rose-800 mb-2">
                      احصلي على هدية مجانية!
                    </h3>
                    <p className="text-rose-600 mb-4 text-sm">
                      لا تضيعي وقت الانتظار! اضغطي على الزر أدناه للمطالبة
                      بهديتك المجانية مع طلبك.
                    </p>
                    <button
                      onClick={handleClaimGift}
                      className="w-full bg-[#A73A5D] text-white py-3 rounded-2xl font-bold text-lg hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105"
                    >
                      أريد هديتي المجانية! ✨
                    </button>
                  </div>

                  {/* Phase 1: Dermatologist Analysis with Jokes */}
                  {pollingPhase === 1 && (
                    <div className="space-y-6">
                      {/* Main Analysis Card */}
                      <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-purple-200 shadow-xl">
                        <div className="text-center mb-6">
                          <div className="w-20 h-20 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-3">
                            <Brain className="w-10 h-10 text-white animate-pulse" />
                          </div>
                          <h2 className="text-3xl font-bold text-purple-800 mb-2">
                            🩺 الطبيب المختص يحلل بشرتك
                          </h2>
                          <p className="text-purple-600 text-lg">
                            الذكاء الاصطناعي الطبي يعمل بدقة عالية...
                          </p>
                        </div>

                        {/* Rotating Jokes Card - Made More Prominent */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border-2 border-yellow-200 shadow-lg mb-6">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                              {React.createElement(
                                aiSteps[currentJokeIndex].icon,
                                {
                                  className: "w-8 h-8 text-white animate-spin",
                                }
                              )}
                            </div>
                            <h3 className="text-2xl font-bold text-orange-800 mb-3 animate-pulse">
                              {aiSteps[currentJokeIndex].title}
                            </h3>
                            <p className="text-orange-700 text-lg leading-relaxed font-medium">
                              {aiSteps[currentJokeIndex].subtitle}
                            </p>
                          </div>

                          {/* Animated dots */}
                          <div className="flex justify-center items-center gap-1 mt-4">
                            <div
                              className="w-3 h-3 bg-orange-400 rounded-full animate-bounce"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="w-3 h-3 bg-orange-400 rounded-full animate-bounce"
                              style={{ animationDelay: "200ms" }}
                            ></div>
                            <div
                              className="w-3 h-3 bg-orange-400 rounded-full animate-bounce"
                              style={{ animationDelay: "400ms" }}
                            ></div>
                          </div>
                        </div>

                        {/* Free Gift Button */}
                        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border-2 border-dashed border-pink-300 shadow-lg mt-6 text-center transition-all duration-300 hover:shadow-xl hover:border-pink-400">
                          <div className="w-16 h-16 bg-[#A73A5D] rounded-full flex items-center justify-center mx-auto mb-4">
                            <Gift className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-[#2D1B1F] mb-2">
                            احصلي على هدية مجانية!
                          </h3>
                          <p className="text-[#6B4E58] mb-4 text-sm">
                            لا تضيعي وقت الانتظار! اضغطي على الزر أدناه للمطالبة
                            بهديتك المجانية مع طلبك.
                          </p>
                          <button
                            onClick={handleClaimGift}
                            className="w-full bg-[#A73A5D] text-white py-3 rounded-2xl font-bold text-lg hover:bg-[#8F2F4D] hover:shadow-md transition-all duration-200 hover:scale-105"
                          >
                            أريد هديتي المجانية! ✨
                          </button>
                        </div>

                        {/* Phase 1 Progress Indicator */}
                        <div className="bg-white rounded-xl p-4 border border-[#A73A5D]/10">
                          <div className="flex items-center justify-center gap-3">
                            <Search className="w-6 h-6 text-purple-600 animate-pulse" />
                            <span className="text-purple-700 font-semibold">
                              جاري التحليل الطبي المتخصص...
                            </span>
                            <div className="flex gap-1">
                              <div
                                className="w-2 h-2 bg-[#A73A5D] rounded-full animate-pulse"
                                style={{ animationDelay: "0ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-[#A73A5D] rounded-full animate-pulse"
                                style={{ animationDelay: "300ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-[#A73A5D] rounded-full animate-pulse"
                                style={{ animationDelay: "600ms" }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Phase 2: Product Search (No Jokes) */}
                  {pollingPhase === 2 && (
                    <div className="space-y-6">
                      {/* Dermatologist Results Card */}
                      {dermatologistResults && (
                        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-green-200 shadow-xl">
                          <button
                            onClick={() =>
                              setShowSkinReportPreview(!showSkinReportPreview)
                            }
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl hover:shadow-md transition-all duration-300 hover:scale-105"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                                <Check className="w-6 h-6 text-white" />
                              </div>
                              <div className="text-right">
                                <h3 className="text-xl font-bold text-green-800">
                                  ✅ تم التحليل الطبي بنجاح!
                                </h3>
                                <p className="text-green-600">
                                  اضغطي هنا لرؤية ما اكتشفناه عن بشرتك
                                </p>
                              </div>
                            </div>
                            <div
                              className={`transform transition-transform duration-300 ${showSkinReportPreview ? "rotate-180" : ""
                                }`}
                            >
                              <span className="text-2xl">⬇️</span>
                            </div>
                          </button>

                          {showSkinReportPreview && (
                            <div className="mt-4 p-4 bg-green-50 rounded-2xl space-y-4 animate-fade-in shadow-inner">
                              <h4 className="font-bold text-green-800 text-lg flex items-center gap-2">
                                <Sparkles className="w-5 h-5 animate-pulse" />
                                تحليل بشرتك الشخصي
                              </h4>
                              <div className="bg-white rounded-xl p-4 border border-green-200">
                                <SkinAnalysisDisplay
                                  analysisData={dermatologistResults}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Product Search Card */}
                      <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-blue-200 shadow-xl">
                        <div className="text-center mb-6">
                          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                            <ShoppingCart className="w-10 h-10 text-white animate-spin" />
                          </div>
                          <h2 className="text-3xl font-bold text-blue-800 mb-2">
                            🛍️ البحث عن المنتجات المثالية
                          </h2>
                          <p className="text-blue-600 text-lg">
                            نبحث في موقعنا عن أفضل المنتجات لبشرتك...
                          </p>
                        </div>

                        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                          <div className="flex items-center justify-center gap-4 mb-4">
                            <Database className="w-8 h-8 text-blue-600 animate-pulse" />
                            <span className="text-blue-700 font-semibold text-lg">
                              جاري مطابقة المنتجات مع احتياجاتك
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mb-4">
                            {["تنظيف", "ترطيب", "حماية"].map((step, index) => (
                              <div
                                key={index}
                                className="bg-white rounded-lg p-3 text-center border border-blue-200"
                              >
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <Sparkles className="w-4 h-4 text-blue-600 animate-pulse" />
                                </div>
                                <span className="text-blue-700 text-sm font-medium">
                                  {step}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="text-center">
                            <div className="flex justify-center items-center gap-1">
                              <div
                                className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
                                style={{ animationDelay: "0ms" }}
                              ></div>
                              <div
                                className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
                                style={{ animationDelay: "200ms" }}
                              ></div>
                              <div
                                className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
                                style={{ animationDelay: "400ms" }}
                              ></div>
                            </div>
                            <p className="text-blue-600 text-sm mt-2">
                              لن يستغرق الأمر طويلاً...
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error State */}
                  {pollingError && (
                    <div className="bg-red-50 border border-red-200 rounded-3xl p-6 text-center shadow-xl">
                      <div className="text-4xl mb-3">😔</div>
                      <h3 className="text-xl font-bold text-red-800 mb-2">
                        حدث خطأ في التحليل
                      </h3>
                      <p className="text-red-600 mb-4">{pollingError}</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="bg-red-500 text-white px-6 py-2 rounded-xl hover:bg-red-600 transition-colors"
                      >
                        إعادة المحاولة
                      </button>
                    </div>
                  )}

                  {/* Timeout State */}
                  {showTimeout && (
                    <div className="bg-orange-50 border border-orange-200 rounded-3xl p-6 text-center shadow-xl">
                      <div className="text-4xl mb-3">⏰</div>
                      <h3 className="text-xl font-bold text-orange-800 mb-2">
                        انتهت المهلة الزمنية
                      </h3>
                      <p className="text-orange-600 mb-4">
                        نأسف للانتظار! حدث شيء غير متوقع، لكن لا تقلقي - فريقنا
                        سيتواصل معك خلال 24 ساعة لإكمال تحليل بشرتك.
                      </p>
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => window.location.reload()}
                          className="bg-orange-500 text-white px-6 py-2 rounded-xl hover:bg-orange-600 transition-colors"
                        >
                          إعادة المحاولة
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Keep the old analyzing state for backward compatibility */}
              {analyzing && !realTimeAnalyzing && (
                <div className="bg-blue-50 border border-blue-200 rounded-3xl p-6 text-center shadow-xl">
                  <div className="text-2xl mb-3">🔄</div>
                  <h3 className="text-lg font-bold text-blue-800 mb-2">
                    النظام القديم يعمل
                  </h3>
                  <p className="text-blue-600">
                    استخدمي النظام الجديد للحصول على تجربة أفضل
                  </p>
                </div>
              )}

              {/* Results - Revamped Checkout Style */}
              {showRoutine && (
                <div className="space-y-6">
                  {/* Error Fallback Message */}
                  {hasDataError && (
                    <div className="bg-red-50 border border-red-200 rounded-3xl p-6 text-center shadow-xl">
                      <div className="text-4xl mb-3">😔</div>
                      <h3 className="text-xl font-bold text-red-800 mb-2">
                        عذراً، واجهنا مشكلة تقنية
                      </h3>
                      <p className="text-red-600 mb-4">
                        فريقنا سيتواصل معك قريباً لحل هذه المشكلة
                      </p>
                      <div className="text-sm text-red-500">
                        يمكنك التواصل معنا مباشرة عبر واتساب أو الهاتف
                      </div>
                    </div>
                  )}

                  {!hasDataError && (
                    <div className="space-y-12">
                      {/* Hair Routine Display */}
                      {hairRoutineData && (
                        <HairRoutineDisplay
                          hairData={consultationData?.consultation?.hair_user_consultations?.product_offer || consultationData?.consultation?.products_offered}
                          isAiGenerated={true}
                          onProductQuestion={(productName) => {
                            setChatConfig({
                              startOpen: true,
                              initialMessage: productName
                                ? `أريد معرفة المزيد عن ${productName} - ما فوائده وكيف أستخدمه؟`
                                : "أريد معرفة المزيد عن منتجاتي المخصصة",
                            });
                          }}
                          chatPromptShown={chatPromptShown}
                          setChatPromptShown={setChatPromptShown}
                        />
                      )}

                      {/* Skin Routine Display */}
                      {aiGeneratedProducts && (
                        <div className="space-y-6">
                          <div className="text-center">
                            <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                              <ShoppingCart className="w-10 h-10 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold text-[#2D1B1F] mb-2">
                              🛒 منتجاتك المخصصة للبشرة
                            </h2>
                            <p className="text-[#6B4E58]">
                              روتين العناية المثالي لبشرتك
                            </p>
                            {displayRoutines.isAiGenerated && (
                              <div className="mt-2 inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs">
                                <Brain className="w-4 h-4" />
                                مخصص بالذكاء الاصطناعي
                              </div>
                            )}
                          </div>

                          {/* Chat Engagement - Top of Products */}
                          {!chatPromptShown.userInteracted && (
                            <div className="bg-white rounded-3xl p-4 border border-[#A73A5D]/10 shadow-sm animate-fade-in">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl animate-bounce">
                                    💡
                                  </span>
                                  <div>
                                    <h3 className="font-bold text-[#2D1B1F] text-sm">
                                      عندك أسئلة عن منتجاتك؟
                                    </h3>
                                    <p className="text-rose-600 text-xs">
                                      اسألي عن الفوائد وطريقة الاستخدام
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      setChatConfig({
                                        startOpen: true,
                                        initialMessage:
                                          "أريد معرفة المزيد عن المنتجات المخصصة لي وكيفية استخدامها",
                                      });
                                      setChatPromptShown((prev) => ({
                                        ...prev,
                                        userInteracted: true,
                                        topOfPage: true,
                                      }));
                                    }}
                                    className="bg-[#A73A5D] text-white text-sm px-4 py-2 rounded-full hover:bg-[#8F2F4D] transition-all flex items-center gap-1"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                    اسألي الآن
                                  </button>
                                  <button
                                    onClick={() =>
                                      setChatPromptShown((prev) => ({
                                        ...prev,
                                        topOfPage: true,
                                      }))
                                    }
                                    className="text-gray-400 hover:text-gray-600 text-lg"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Morning Routine */}
                          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-pink-200 shadow-xl">
                            <h3 className="text-xl font-bold text-rose-700 mb-4 flex items-center gap-2">
                              <span className="text-2xl">☀️</span>
                              روتين الصباح
                            </h3>
                            <div className="space-y-4">
                              {displayRoutines.morning.map((item, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-yellow-200 hover:shadow-md transition-all duration-300"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-xl bg-yellow-100 flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                                      {item.isAiGenerated &&
                                        item.image &&
                                        item.image.startsWith("http") ? (
                                        <img
                                          src={item.image}
                                          alt={item.imageAlt || item.product}
                                          className="w-full h-full object-cover rounded-lg"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display =
                                              "flex";
                                          }}
                                        />
                                      ) : null}
                                      <div
                                        className={`text-3xl ${item.isAiGenerated &&
                                          item.image &&
                                          item.image.startsWith("http")
                                          ? "hidden"
                                          : "flex"
                                          } items-center justify-center w-full h-full`}
                                      >
                                        {item.isAiGenerated ? "🫧" : item.image}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs text-yellow-600 font-semibold mb-1">
                                      الخطوة {index + 1}
                                    </div>
                                    <h4 className="font-bold text-gray-800 text-sm leading-tight mb-1">
                                      {item.step}
                                    </h4>
                                    <p className="text-xs text-gray-600 mb-2">
                                      {item.product}
                                    </p>
                                    {item.link && (
                                      <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline block mb-2"
                                      >
                                        عرض المنتج
                                      </a>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <span className="text-lg font-bold text-green-600">
                                        {item.price}{" "}
                                        {displayRoutines.isAiGenerated
                                          ? "د.ع"
                                          : "SAR"}
                                      </span>
                                      <button
                                        onClick={() => {
                                          setChatConfig({
                                            startOpen: true,
                                            initialMessage: `أريد معرفة المزيد عن ${item.product} - ما فوائده وكيف أستخدمه؟`,
                                          });
                                          setChatPromptShown((prev) => ({
                                            ...prev,
                                            userInteracted: true,
                                          }));
                                        }}
                                        className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full hover:bg-yellow-200 transition-all flex items-center gap-1"
                                      >
                                        <MessageCircle className="w-3 h-3" />
                                        سؤال
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center shadow-md">
                                      <span className="text-white font-bold text-sm">
                                        {index + 1}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Evening Routine */}
                          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-pink-200 shadow-xl">
                            <h3 className="text-xl font-bold text-rose-700 mb-4 flex items-center gap-2">
                              <span className="text-2xl">🌙</span>
                              روتين المساء
                            </h3>
                            <div className="space-y-4">
                              {displayRoutines.evening.map((item, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-blue-200 hover:shadow-md transition-all duration-300"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-xl bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                                      {item.isAiGenerated &&
                                        item.image &&
                                        item.image.startsWith("http") ? (
                                        <img
                                          src={item.image}
                                          alt={item.imageAlt || item.product}
                                          className="w-full h-full object-cover rounded-lg"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display =
                                              "flex";
                                          }}
                                        />
                                      ) : null}
                                      <div
                                        className={`text-3xl ${item.isAiGenerated &&
                                          item.image &&
                                          item.image.startsWith("http")
                                          ? "hidden"
                                          : "flex"
                                          } items-center justify-center w-full h-full`}
                                      >
                                        {item.isAiGenerated ? "🌙" : item.image}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs text-blue-600 font-semibold mb-1">
                                      الخطوة {index + 1}
                                    </div>
                                    <h4 className="font-bold text-gray-800 text-sm leading-tight mb-1">
                                      {item.step}
                                    </h4>
                                    <p className="text-xs text-gray-600 mb-2">
                                      {item.product}
                                    </p>
                                    {item.link && (
                                      <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline block mb-2"
                                      >
                                        عرض المنتج
                                      </a>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <span className="text-lg font-bold text-green-600">
                                        {item.price}{" "}
                                        {displayRoutines.isAiGenerated
                                          ? "د.ع"
                                          : "SAR"}
                                      </span>
                                      <button
                                        onClick={() => {
                                          setChatConfig({
                                            startOpen: true,
                                            initialMessage: `أريد معرفة المزيد عن ${item.product} - ما فوائده وكيف أستخدمه؟`,
                                          });
                                          setChatPromptShown((prev) => ({
                                            ...prev,
                                            userInteracted: true,
                                          }));
                                        }}
                                        className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full hover:bg-blue-200 transition-all flex items-center gap-1"
                                      >
                                        <MessageCircle className="w-3 h-3" />
                                        سؤال
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-md">
                                      <span className="text-white font-bold text-sm">
                                        {index + 1}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Chat Engagement - End of Products List */}
                          {!chatPromptShown.userInteracted &&
                            !chatPromptShown.topOfPage && (
                              <div className="bg-white rounded-3xl p-5 border border-[#A73A5D]/10 shadow-sm animate-fade-in">
                                <div className="text-center mb-4">
                                  <span className="text-3xl mb-2 block">💜</span>
                                  <h3 className="font-bold text-purple-800 text-lg">
                                    أي شي بخاطرچ اسألينا هنا
                                  </h3>
                                  <p className="text-purple-600 text-sm">
                                    {" "}
                                    لا تستحين، گولي وأنا أجـاوبج 🌸
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <button
                                    onClick={() => {
                                      setChatConfig({
                                        startOpen: true,
                                        initialMessage:
                                          "هل يمكنني معرفة المزيد عن فوائد هذه المنتجات وكيفية تأثيرها على بشرتي؟",
                                      });
                                      setChatPromptShown((prev) => ({
                                        ...prev,
                                        userInteracted: true,
                                        endOfProducts: true,
                                      }));
                                    }}
                                    className="bg-[#A73A5D]/10 text-[#A73A5D] py-3 px-4 rounded-xl font-medium hover:bg-[#A73A5D]/20 transition-all flex items-center justify-center gap-2 text-sm"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                    اسألي عن الفوائد
                                  </button>

                                  <button
                                    onClick={() => {
                                      setChatConfig({
                                        startOpen: true,
                                        initialMessage:
                                          "كيف أستخدم هذه المنتجات بالطريقة الصحيحة؟ ما هو الترتيب المناسب؟",
                                      });
                                      setChatPromptShown((prev) => ({
                                        ...prev,
                                        userInteracted: true,
                                        endOfProducts: true,
                                      }));
                                    }}
                                    className="bg-blue-100 text-blue-700 py-3 px-4 rounded-xl font-medium hover:bg-blue-200 transition-all flex items-center justify-center gap-2 text-sm"
                                  >
                                    <Lightbulb className="w-4 h-4" />
                                    طريقة الاستخدام
                                  </button>
                                </div>

                                <div className="text-center mt-3">
                                  <button
                                    onClick={() =>
                                      setChatPromptShown((prev) => ({
                                        ...prev,
                                        endOfProducts: true,
                                      }))
                                    }
                                    className="text-gray-400 hover:text-gray-600 text-sm"
                                  >
                                    إخفاء هذه الرسالة
                                  </button>
                                </div>
                                {/* Skin Routine End */}
                              </div>
                            )}

                          {/* Total Summary - Only for Skin Care */}
                          {aiGeneratedProducts && consultationType !== 'Hair Care' && (
                            <div className="bg-white rounded-3xl p-6 border-2 border-green-200 shadow-sm">
                              <div className="text-center mb-4">
                                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <Sparkles className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-green-800 mb-1">
                                  💚 ملخص الطلبية
                                </h3>
                                <p className="text-green-600 text-sm">
                                  روتين كامل لمدة 3 أشهر
                                </p>
                              </div>

                              <div className="space-y-3 mb-6">
                                {(() => {
                                  const pricing = calculatePricing(
                                    displayRoutines.totalPrice,
                                    orderData.city,
                                    displayRoutines.isAiGenerated
                                  );
                                  return (
                                    <div className="space-y-4">
                                      {/* Auto-applied Discount with Strikethrough */}
                                      <div className="text-center bg-green-50 p-4 rounded-xl border-2 border-green-200">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                          <span className="text-2xl">🎉</span>
                                          <span className="text-green-700 font-bold">
                                            خصم تلقائي "wondura" مُطبق!
                                          </span>
                                        </div>

                                        {/* Original Price with Strikethrough */}
                                        <div className="text-center mb-2">
                                          <span className="text-gray-500 text-xl line-through decoration-red-500 decoration-2">
                                            {pricing.subtotal}{" "}
                                            {displayRoutines.isAiGenerated
                                              ? "د.ع"
                                              : "SAR"}
                                          </span>
                                        </div>

                                        {/* Discounted Price */}
                                        <div className="text-center">
                                          <div className="text-3xl font-bold text-green-600">
                                            {pricing.afterDiscount}{" "}
                                            {displayRoutines.isAiGenerated
                                              ? "د.ع"
                                              : "SAR"}
                                          </div>
                                          <div className="text-sm text-green-600 mt-1">
                                            وفرتِ {pricing.discountAmount}{" "}
                                            {displayRoutines.isAiGenerated
                                              ? "د.ع"
                                              : "SAR"}{" "}
                                            (%{pricing.discountPercent})
                                          </div>
                                        </div>
                                      </div>

                                      {/* Delivery Fee and Final Total (only if delivery fee applies) */}
                                      {pricing.showDeliveryFee && (
                                        <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-200">
                                          <div className="flex justify-between items-center mb-2">
                                            <span className="text-orange-700 font-semibold">
                                              رسوم التوصيل:
                                            </span>
                                            <span className="text-orange-700 font-bold">
                                              +{pricing.deliveryFee} د.ع
                                            </span>
                                          </div>
                                          <div className="border-t border-orange-200 pt-2">
                                            <div className="flex justify-between items-center">
                                              <span className="text-xl font-bold text-gray-800">
                                                المجموع النهائي:
                                              </span>
                                              <div className="text-2xl font-bold text-gray-800">
                                                {pricing.finalTotal} د.ع
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>

                              {(() => {
                                const pricing = calculatePricing(
                                  displayRoutines.totalPrice,
                                  orderData.city,
                                  displayRoutines.isAiGenerated
                                );

                                if (
                                  !pricing.showDeliveryFee &&
                                  displayRoutines.isAiGenerated
                                ) {
                                  return (
                                    <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-yellow-50 rounded-xl border border-yellow-200 shadow-inner">
                                      <span className="text-2xl">🚚</span>
                                      <span className="text-sm font-semibold text-yellow-700">
                                        توصيل مجاني خلال 48 ساعة في بغداد والبصرة.
                                        المناطق الأخرى خلال 3-5 أيام
                                      </span>
                                    </div>
                                  );
                                } else if (pricing.showDeliveryFee) {
                                  return (
                                    <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-orange-50 rounded-xl border border-orange-200 shadow-inner">
                                      <span className="text-2xl">🚚</span>
                                      <span className="text-sm font-semibold text-orange-700">
                                        رسوم التوصيل: {pricing.deliveryFee} د.ع |
                                        التوصيل خلال 48 ساعة في بغداد والبصرة. المناطق
                                        الأخرى خلال 3-5 أيام
                                      </span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}

                          {aiGeneratedProducts && (
                            <div className="space-y-6">
                              {/* Action Buttons */}
                              <div className="flex gap-3">
                                <button
                                  onClick={handleOrder}
                                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-2xl font-bold text-lg hover:shadow-lg transition-all duration-300 hover:from-green-600 hover:to-emerald-700 flex items-center justify-center gap-2"
                                >
                                  <ShoppingCart className="w-6 h-6" />
                                  اطلبي الآن -{" "}
                                  {(() => {
                                    const pricing = calculatePricing(
                                      displayRoutines.totalPrice,
                                      "",
                                      displayRoutines.isAiGenerated
                                    );
                                    return pricing.finalTotal;
                                  })()}{" "}
                                  {displayRoutines.isAiGenerated ? "د.ع" : "SAR"}
                                </button>
                              </div>

                              {/* Update Products Button - Below Place Order */}
                              <div className="mt-4">
                                <button
                                  onClick={refreshProducts}
                                  disabled={refreshingProducts}
                                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-4 rounded-2xl font-bold text-base transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {refreshingProducts ? (
                                    <>
                                      <RefreshCw className="w-5 h-5 animate-spin" />
                                      جاري التحديث...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-5 h-5" />
                                      تحديث المنتجات
                                    </>
                                  )}
                                </button>

                                {/* Refresh Error Message */}
                                {refreshError && (
                                  <div className="mt-3 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                    {refreshError}
                                  </div>
                                )}

                                {/* Refresh Success Message */}
                                {refreshSuccess && (
                                  <div className="mt-3 text-green-600 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2 animate-fade-in">
                                    ✅ تم تحديث المنتجات بنجاح!
                                  </div>
                                )}
                              </div>

                              <div className="flex gap-3">
                                <button
                                  onClick={() => {
                                    setChatConfig({
                                      startOpen: true,
                                      initialMessage: "لدي استفسار عن المنتجات في طلبي",
                                    });
                                    setChatPromptShown((prev) => ({
                                      ...prev,
                                      userInteracted: true,
                                    }));
                                  }}
                                  className="flex-1 border-2 border-pink-300 text-rose-700 py-3 rounded-2xl font-bold text-base hover:border-pink-400 transition-all duration-300 hover:bg-pink-50 flex items-center justify-center gap-2 relative overflow-hidden"
                                >
                                  <MessageCircle className="w-5 h-5" />
                                  <span>استفسري عن منتجاتك</span>
                                  <div className="absolute inset-0 bg-pink-100 opacity-0 hover:opacity-20 transition-opacity duration-300"></div>
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Analysis Reports */}
                          {(dermatologistResults || dermatologistResultsHair) && (
                            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 border border-pink-200 shadow-xl">
                              <button
                                onClick={() => setShowSkinReport(!showSkinReport)}
                                className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-[#A73A5D]/10 hover:shadow-md transition-all duration-300 hover:scale-105"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="text-2xl animate-bounce">🔬</div>
                                  <div className="text-right">
                                    <h3 className="text-lg font-bold text-rose-800">
                                      اعرفي أكثر عن نتائج تحليلك
                                    </h3>
                                    <p className="text-sm text-rose-600">
                                      تحليل مفصل لحالتك من قبل الذكاء الاصطناعي
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className={`transform transition-transform duration-300 ${showSkinReport ? "rotate-180" : ""
                                    }`}
                                >
                                  <span className="text-2xl">⬇️</span>
                                </div>
                              </button>

                              {showSkinReport && (
                                <div className="mt-4 p-4 bg-purple-50 rounded-2xl space-y-6 animate-fade-in shadow-inner">
                                  {dermatologistResults && (
                                    <div className="space-y-4">
                                      <h4 className="font-bold text-purple-800 text-lg flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 animate-pulse" />
                                        تحليل بشرتك الشخصي
                                      </h4>

                                      <div className="bg-white p-4 rounded-xl shadow-md">
                                        <SkinAnalysisDisplay
                                          analysisData={dermatologistResults}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {dermatologistResultsHair && (
                                    <div className="space-y-4 pt-4 border-t border-purple-200">
                                      <h4 className="font-bold text-purple-800 text-lg flex items-center gap-2">
                                        <Brain className="w-5 h-5 animate-pulse" />
                                        تحليل شعرك الشخصي
                                      </h4>

                                      <div className="bg-white p-4 rounded-xl shadow-md">
                                        <SkinAnalysisDisplay
                                          analysisData={dermatologistResultsHair}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {showAddressPopup && (
                    <div
                      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
                      dir="rtl"
                    >
                      <div className="bg-white rounded-3xl p-8 mx-4 shadow-2xl max-w-md w-full">
                        <div className="text-center mb-6">
                          <div className="text-4xl mb-3">📍</div>
                          <h3 className="text-2xl font-bold text-gray-800 mb-2">
                            تأكيد العنوان
                          </h3>
                          <p className="text-gray-600">
                            يرجى إدخال عنوان التوصيل
                          </p>
                        </div>

                        <div className="space-y-4">
                          {/* City Dropdown */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              المحافظة
                            </label>
                            <select
                              value={orderData.city}
                              onChange={(e) =>
                                setOrderData((prev) => ({
                                  ...prev,
                                  city: e.target.value,
                                }))
                              }
                              className="w-full p-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-green-300 focus:border-transparent text-right"
                            >
                              {iraqiCities.map((city, index) => (
                                <option key={index} value={city}>
                                  {city}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Address Input */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              العنوان التفصيلي
                            </label>
                            <input
                              type="text"
                              placeholder="اكتبي العنوان التفصيلي..."
                              value={orderData.address}
                              onChange={(e) =>
                                setOrderData((prev) => ({
                                  ...prev,
                                  address: e.target.value,
                                }))
                              }
                              className="w-full p-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-green-300 focus:border-transparent text-right"
                            />
                          </div>
                        </div>

                        {/* Order Summary */}
                        <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200">
                          {(() => {
                            const pricing = calculatePricing(
                              displayRoutines.totalPrice,
                              orderData.city,
                              displayRoutines.isAiGenerated
                            );
                            return (
                              <div className="space-y-3">
                                {/* Auto-applied Discount with Strikethrough */}
                                <div className="text-center bg-green-100 p-3 rounded-lg border border-green-300">
                                  <div className="flex items-center justify-center gap-1 mb-1">
                                    <span className="text-lg">🎉</span>
                                    <span className="text-green-700 font-bold text-sm">
                                      خصم "wondura" مُطبق!
                                    </span>
                                  </div>

                                  {/* Original Price with Strikethrough */}
                                  <div className="text-center mb-1">
                                    <span className="text-gray-500 text-lg line-through decoration-red-500 decoration-2">
                                      {pricing.subtotal}{" "}
                                      {displayRoutines.isAiGenerated
                                        ? "د.ع"
                                        : "SAR"}
                                    </span>
                                  </div>

                                  {/* Discounted Price */}
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                      {pricing.afterDiscount}{" "}
                                      {displayRoutines.isAiGenerated
                                        ? "د.ع"
                                        : "SAR"}
                                    </div>
                                    <div className="text-xs text-green-600 mt-1">
                                      وفرتِ {pricing.discountAmount}{" "}
                                      {displayRoutines.isAiGenerated
                                        ? "د.ع"
                                        : "SAR"}
                                    </div>
                                  </div>
                                </div>

                                {/* Delivery Fee and Final Total (only if delivery fee applies) */}
                                {pricing.showDeliveryFee && (
                                  <div className="bg-orange-100 p-3 rounded-lg border border-orange-300">
                                    <div className="flex justify-between items-center mb-2 text-sm">
                                      <span className="text-orange-700 font-semibold">
                                        رسوم التوصيل:
                                      </span>
                                      <span className="text-orange-700 font-bold">
                                        +{pricing.deliveryFee} د.ع
                                      </span>
                                    </div>
                                    <div className="border-t border-orange-200 pt-2">
                                      <div className="flex justify-between items-center">
                                        <span className="font-semibold text-gray-700">
                                          المجموع النهائي:
                                        </span>
                                        <span className="text-xl font-bold text-gray-800">
                                          {pricing.finalTotal} د.ع
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Delivery Information */}
                        {(() => {
                          const pricing = calculatePricing(
                            displayRoutines.totalPrice,
                            orderData.city,
                            displayRoutines.isAiGenerated
                          );

                          if (
                            !pricing.showDeliveryFee &&
                            displayRoutines.isAiGenerated
                          ) {
                            return (
                              <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                                <div className="flex items-center gap-2 text-blue-700">
                                  <span className="text-lg">🚚</span>
                                  <span className="text-sm font-semibold">
                                    سياسة التوصيل المجاني:
                                  </span>
                                </div>
                                <div className="text-xs text-blue-600 mt-1">
                                  • بغداد والبصرة: خلال 48 ساعة
                                  <br />• المناطق الأخرى: خلال 3-5 أيام
                                </div>
                              </div>
                            );
                          } else if (pricing.showDeliveryFee) {
                            return (
                              <div className="mt-4 p-3 bg-orange-50 rounded-xl border border-orange-200">
                                <div className="flex items-center gap-2 text-orange-700">
                                  <span className="text-lg">🚚</span>
                                  <span className="text-sm font-semibold">
                                    سياسة التوصيل:
                                  </span>
                                </div>
                                <div className="text-xs text-orange-600 mt-1">
                                  • بغداد: رسوم التوصيل 4000 د.ع - خلال 48 ساعة
                                  <br />
                                  • البصرة: رسوم التوصيل 5000 د.ع - خلال 48 ساعة
                                  <br />• المناطق الأخرى: رسوم التوصيل 6000 د.ع
                                  - خلال 3-5 أيام
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => setShowAddressPopup(false)}
                            className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:border-gray-400 transition-all duration-300"
                          >
                            إلغاء
                          </button>
                          <button
                            onClick={handleAddressSubmit}
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300 hover:from-green-600 hover:to-emerald-700"
                          >
                            تأكيد الطلب
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {orderSuccess && (
                    <div
                      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in"
                      dir="rtl"
                    >
                      <div className="bg-white rounded-3xl p-8 text-center mx-4 shadow-2xl animate-bounce">
                        <div className="text-6xl mb-4 animate-bounce">🎉</div>
                        <h3 className="text-2xl font-bold text-green-600 mb-2 animate-pulse">
                          تم بنجاح!
                        </h3>
                        <p className="text-gray-600">سنتواصل معك قريباً</p>
                        <div className="mt-4 flex justify-center gap-1">
                          <div
                            className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
                            style={{ animationDelay: "200ms" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-green-400 rounded-full animate-bounce"
                            style={{ animationDelay: "400ms" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Completion Page - Renders independently of step for simplified flow */}
          {showCompletionPage && (
            <CompletionPage
              onShare={handleShare}
              onContactSupport={handleContactSupport}
              onStartNew={handleStartNewConsultation}
            />
          )}

          {/* Chat Widget - Only show after step 3 or when user has data */}
          {showChat && (
            <ChatWidget
              currentStep={currentStep}
              orderData={orderData}
              displayRoutines={displayRoutines}
              consultationData={consultationData}
              isVisible={showChat}
              startOpen={chatConfig.startOpen}
              initialMessage={chatConfig.initialMessage}
              onClose={() =>
                setChatConfig({ startOpen: false, initialMessage: "" })
              }
              enableAnimation={showRoutine} // Enable animation only on product page
            />
          )}

          {/* Toast Notification for webhook status */}
          {toastStatus && (
            <ToastNotification
              status={toastStatus}
              message={toastMessage}
              onRetry={webhookRetryCallback}
              onDismiss={() => setToastStatus(null)}
            />
          )}
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default WonderBeautiesConsultation;
